import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Clock3, Zap, CheckCircle2, ArrowRight, ArrowLeft, CalendarDays, Circle,
  IndianRupee, ShieldCheck, CalendarCheck2, Home, Pencil, Info,
  Plus, Minus, X, XCircle,
} from "lucide-react";
import { getServiceImage, getCategoryHeroImage, DEFAULT_HERO_IMAGE } from "../../config/serviceImages";
import { isUserLoggedIn } from "../common/Header";
import config from "../../config";
import { useCityPrice } from "../../hooks/useCityPrice";

// Same QAS_* cards used on the Instant Home Services catalog page —
// reused here as a 21-item showcase for the Home Cleaning step (20 QAS_*
// photos + Bathroom Cleaning, the one home-cleaning task with no QAS asset).
import qas_rangoli_img          from "../../components/images/stage_01/QAS_IHRA.png";
import qas_fish_tank_img        from "../../components/images/stage_01/QAS_IHFT.png";
import qas_showcase_img         from "../../components/images/stage_01/QAS_IHDC.png";
import qas_chimney_img          from "../../components/images/stage_01/QAS_IHCC.png";
import qas_microwave_oven_img   from "../../components/images/stage_01/QAS_IHMO.png";
import qas_kitchen_cabinet_img  from "../../components/images/stage_01/QAS_IHKC.png";
import qas_window_glass_img     from "../../components/images/stage_01/QAS_IHWG.png";
import qas_wardrobe_img         from "../../components/images/stage_01/QAS_IHWA.png";
import qas_bedroom_arrange_img  from "../../components/images/stage_01/QAS_IHBA.png";
import qas_outside_spaces_img   from "../../components/images/stage_01/QAS_IHBE.png";
import qas_fan_switchboard_img  from "../../components/images/stage_01/QAS_IHCF.png";
import qas_packing_unpacking_img from "../../components/images/stage_01/QAS_IHPU.png";
import qas_furniture_img        from "../../components/images/stage_01/QAS_IHFC.png";
import qas_dusting_wiping_img   from "../../components/images/stage_01/QAS_IHDW.png";
import qas_sweeping_mopping_img from "../../components/images/stage_01/QAS_IHSM.png";
import qas_food_prep_img        from "../../components/images/stage_01/QAS_IHFP.png";
import qas_utensil_img          from "../../components/images/stage_01/QAS_IHCU.png";
import qas_kitchen_cleaning_img from "../../components/images/stage_01/QAS_IHCK.png";
import qas_fridge_img           from "../../components/images/stage_01/QAS_IHRC.png";
import bathroom_cleaning_showcase_img from "../../components/images/stage_01/bathroom_cleaning.png";

// Switch & Socket's 5-option list (see SWITCH_SOCKET_OPTIONS below) —
// Switches for the two switch rows, D_Wall_Socket for the two socket rows.
// Consultation keeps the service's own existing image (switchSocketImg,
// read live from the catalog), so no import needed for it here.
import switchesRowImg   from "../../components/images/stage_01/Switches.png";
import dWallSocketRowImg from "../../components/images/stage_01/D_Wall_Socket.png";
import plugRowImg        from "../../components/images/stage_01/3_pin_plug.png";
import consultationRowImg from "../../components/images/stage_01/Electrican_labour.png";

// Home Cleaning's Choose Service step — informational showcase, not a
// picker (see HOME_CLEANING_SCOPE's "flat price per visit duration, not
// per service" note: which services you tick never changed the price, so
// requiring a selection click was just friction). 21 = all 20 QAS_* Quick
// Services Access photos + Bathroom Cleaning (the one with no QAS asset).
const HOME_CLEANING_SHOWCASE = [
  { name: "Rangoli Art",                      image: qas_rangoli_img },
  { name: "Fish Tank Cleaning",                image: qas_fish_tank_img },
  { name: "Showcase Cleaning",                 image: qas_showcase_img },
  { name: "Chimney Cleaning",                  image: qas_chimney_img },
  { name: "Microwave & Oven Cleaning",         image: qas_microwave_oven_img },
  { name: "Kitchen Cabinet Cleaning",          image: qas_kitchen_cabinet_img },
  { name: "Window and Glass Cleaning",         image: qas_window_glass_img },
  { name: "Wardrobe Arrangement",              image: qas_wardrobe_img },
  { name: "Bedroom Arrangement",               image: qas_bedroom_arrange_img },
  { name: "Outside Spaces Cleaning",           image: qas_outside_spaces_img },
  { name: "Fan & Ceiling Cleaning",            image: qas_fan_switchboard_img },
  { name: "Packing & Unpacking",               image: qas_packing_unpacking_img },
  { name: "Furniture Cleaning",                image: qas_furniture_img },
  { name: "Dusting & Wiping",                  image: qas_dusting_wiping_img },
  { name: "Sweeping & Mopping",                image: qas_sweeping_mopping_img },
  { name: "Food Preparation",                  image: qas_food_prep_img },
  { name: "Utensil Cleaning",                  image: qas_utensil_img },
  { name: "Kitchen Cleaning",                  image: qas_kitchen_cleaning_img },
  { name: "Fridge Cleaning",                   image: qas_fridge_img },
  { name: "Bathroom Cleaning",                 image: bathroom_cleaning_showcase_img },
];

// Technical category: services/pricing are sourced from the same catalog
// the Instant Electrical Service page uses (config.SERVICE_CONFIG), not
// from the generic backend-seeded schedule-config list — so the two flows
// always offer identical services/prices, only the booking mechanics differ.
const ELECTRICAL_CATEGORY_KEY = config.TECHNICAL_SERVICE_CATEGORY;
const ELECTRICAL_SERVICE_KEY  = config.ELECTRICAL_SERVICE;

// Switch & Socket's own in-page picker — 5 flat choices instead of the
// generic Work Type → Category → Type drill-down every other catalog entry
// uses, per its own two-level workTypes (install/replace/consultation) ×
// item (switch/socket) data in pricing.js. Clicking one opens a focused
// popup (rating → quantity → material → add), not the dedicated full-page
// wizard SwitchSocketBookingPage.js still owns at its own detailRoute.
const SWITCH_SOCKET_OPTIONS = [
  { key: "switch-install", label: "Switch Install",       workTypeKey: "install", itemLabel: "Switch", image: switchesRowImg,
    description: "Installation of new switch (normal/standard)." },
  { key: "switch-replace", label: "Switch Replacement",   workTypeKey: "replace", itemLabel: "Switch", image: switchesRowImg,
    description: "Replace old or damaged switch." },
  { key: "socket-install", label: "Socket Install",       workTypeKey: "install", itemLabel: "Socket", image: dWallSocketRowImg,
    description: "Installation of new socket (normal/standard)." },
  { key: "socket-replace", label: "Socket Replacement",   workTypeKey: "replace", itemLabel: "Socket", image: dWallSocketRowImg,
    description: "Replace old or damaged socket." },
  // Same Normal/Heavy/Not Sure rating pricing as Switch/Socket — no
  // separate plug price list was given.
  { key: "plug-install",   label: "Plug Install",         workTypeKey: "install", itemLabel: "Plug", image: plugRowImg,
    description: "Installation of new plug point (normal/standard)." },
  { key: "plug-replace",   label: "Plug Replacement",     workTypeKey: "replace", itemLabel: "Plug", image: plugRowImg,
    description: "Replace old or damaged plug point." },
  { key: "consultation",   label: "Consultation",         workTypeKey: "consultation", isConsultation: true, image: consultationRowImg,
    description: "Not sure what you need? An electrician inspects and advises." },
];

// Plug Install/Replacement ask one extra question before Type/Quantity/
// Material — which pin count. No separate pricing exists for 2-Pin vs
// 3-Pin, so it's label-only metadata folded into the cart line, same
// Normal/Heavy/Not Sure × Company/Customer material pricing as Switch/Socket.
const PLUG_TYPES = [
  { value: "2pin", label: "2-Pin Plug" },
  { value: "3pin", label: "3-Pin Plug" },
];

// Plug's own lead-in questions, asked before anything else: which vehicle
// it's for, then whether it's a plain household Plug or an EV charging
// Plug — see ssVehicleType/ssPlugCategory above.
const VEHICLE_TYPES = [
  { value: "2w", label: "2 Wheeler" },
  { value: "3w", label: "3 Wheeler" },
  { value: "4w", label: "4 Wheeler" },
];
const PLUG_CATEGORIES = [
  { value: "plug", label: "Plug" },
  { value: "ev_plug", label: "EV Plug" },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const STORAGE_KEY   = "cartItems";

// Only booked 3 days in advance (see backend slot_availability `daysAhead`).
const DAYS_AHEAD = 3;

const DURATIONS = [
  { value: 1, title: "1 Hour", tagline: "Quick & Efficient", icon: Zap },
  { value: 2, title: "2 Hour", tagline: "Deep & Complete",   icon: Clock3 },
];

// Home Cleaning only — what's actually covered changes with the duration
// picked in Step 3 ("schedule"), so this is keyed by duration (1 | 2) and
// re-read live off the `duration` state as the customer clicks between
// 1 Hour / 2 Hour, instead of being a fixed per-category description.
const HOME_CLEANING_SCOPE = {
  1: {
    fixedDuration: "Fixed Duration: 60 Minutes",
    included: [
      "Kitchen Cleaning", "Utensil Cleaning", "Sweeping & Mopping", "Dusting & Wiping",
      "Food Preparation", "Bedroom & Wardrobe Arrangement", "Window & Glass",
    ],
    excluded: [
      "Deep Cleaning", "Heavy Stains / Grease", "Furniture Shifting / Dismantling",
      "Appliance Repair / Heavy Appliance Cleaning", "Plumbing / Drain / Sewage Work",
      "Electrical Work", "Pest Control / Mold Restoration", "Hazardous / Construction Waste",
      "Gardening", "Specialized Cleaning", "High-Level / Unsafe Areas",
      "Permanent Damage Restoration", "Heavy Utensil Cleaning", "Heavy Laundry",
      "Complex Food Preparation", "Glass Repair / Replacement", "Paint / Cement Removal",
      "Waterproofing / Leakage Repair", "Unlisted Services",
    ],
    dailyRoutine: "Basic routine cleaning and household assistance within the fixed 60-minute schedule.",
    customerNotes: [
      { label: "Duration", text: "Fixed 60 minutes." },
      { label: "Scope", text: "Only listed 1-hour services." },
      { label: "Priority", text: "Tell the professional the priority tasks." },
      { label: "Multiple Tasks", text: "Completion depends on available time." },
      { label: "Access", text: "Keep required areas accessible." },
      { label: "Availability", text: "Customer/authorized person should be available." },
      { label: "Valuables", text: "Secure valuables and fragile items." },
      { label: "Damage", text: "Report existing damage before service." },
      { label: "Excluded", text: "Heavy, technical, unsafe and unlisted work." },
    ],
    commonNote: "Service is limited to the booked time and listed scope; excluded or additional work requires a separate service.",
  },
  2: {
    fixedDuration: "Fixed Duration: 120 Minutes",
    included: [
      "Kitchen Cleaning", "Utensil Cleaning", "Sweeping & Mopping", "Dusting & Wiping",
      "Bathroom Cleaning", "Furniture Cleaning", "Balcony & Entrance Cleaning",
      "Kitchen Cabinet Cleaning", "Refrigerator / Microwave / Oven / Grill Cleaning",
      "Chimney / Display Cabinet / Showcase Cleaning", "Steps & Parking Space Cleaning",
      "Fish Tank / Special Cleaning",
    ],
    excluded: [
      "Deep Cleaning", "Heavy Stains / Grease", "Furniture Shifting / Dismantling",
      "Appliance Repair / Heavy Appliance Cleaning", "Plumbing / Drain / Sewage Work",
      "Electrical Work", "Pest Control / Mold Restoration", "Hazardous / Construction Waste",
      "Gardening", "Specialized Cleaning", "High-Level / Unsafe Areas",
      "Permanent Damage Restoration", "Heavy Utensil Cleaning", "Heavy Laundry",
      "Complex Food Preparation", "Glass Repair / Replacement", "Paint / Cement Removal",
      "Waterproofing / Leakage Repair", "Unlisted Services",
    ],
    dailyRoutine: "Basic routine cleaning and household assistance within the fixed 120-minute schedule.",
    customerNotes: [
      { label: "Duration", text: "Fixed 120 minutes." },
      { label: "Scope", text: "Only listed 2-hour services." },
      { label: "Priority", text: "Tell the professional the priority areas." },
      { label: "Multiple Tasks", text: "Completion depends on available time." },
      { label: "Access", text: "Keep required areas accessible." },
      { label: "Availability", text: "Customer/authorized person should be available." },
      { label: "Valuables", text: "Secure valuables and fragile items." },
      { label: "Damage", text: "Report existing damage before service." },
      { label: "Excluded", text: "Deep, heavy, technical, specialized, unsafe and unlisted work." },
    ],
    commonNote: "Service is limited to the booked time and listed scope; excluded or additional work requires a separate service.",
  },
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// 216000 -> "216K", 2300 -> "2.3K", 850 -> "850" — matches the "216K
// reviews" style on the reference design.
const formatReviewCount = (n) => {
  const num = Number(n || 0);
  if (num >= 1000) return `${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}K`;
  return String(num);
};

// URL uses kebab-case ("home-cleaning"); category.key from the backend is
// snake_case ("home_cleaning") and must stay that way — it's sent through
// to place_order() as schedule_category_key unchanged. These only convert
// at the URL boundary.
const slugifyCategory   = (key)  => String(key || "").replace(/_/g, "-");
const unslugifyCategory = (slug) => String(slug || "").replace(/-/g, "_");

const fetchScheduleConfig = async () => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/schedule-config/`);
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load service catalog.");
  return { categories: data?.data?.categories || [], services: data?.data?.services || [] };
};

const fetchSlotAvailability = async (serviceId, duration) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    // includeToday adds today's date to the response (the same seat
    // counts/templates the admin already configures — see config_schedule_booking
    // — nothing new is invented here, today just uses the same slot template
    // as every other day); daysAhead is unchanged so the existing "book up
    // to 3 days ahead" window still shows in full alongside it.
    body: JSON.stringify({ serviceId: String(serviceId), duration, daysAhead: DAYS_AHEAD, includeToday: true }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const formatTodayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

// Today's slots additionally need a "has this time already passed" check —
// every other day in the list is always fully in the future so this never
// applies to them. Same parsing/threshold as InstantSummaryPage.js's "book a
// slot later today" option (at least 1 hour from now; also re-enforced
// server-side in place_order()'s same-day guard).
const parseSlotClockTime = (timeStr) => {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(timeStr || "").trim());
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return { h, min };
};

const slotTimeToday = (timeStr) => {
  const parsed = parseSlotClockTime(timeStr);
  if (!parsed) return null;
  const d = new Date();
  d.setHours(parsed.h, parsed.min, 0, 0);
  return d;
};

const isSlotTooSoon = (slot, date) => {
  if (date !== formatTodayDDMMYYYY()) return false;
  const dt = slotTimeToday(slot?.time);
  if (!dt) return false;
  return dt.getTime() <= Date.now() + 60 * 60 * 1000;
};

const isSlotSelectable = (slot, date) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0 && !isSlotTooSoon(slot, date);

// Four distinct visual states: capacity exhausted (booked) vs explicitly
// disabled (unavailable, e.g. an admin turned it off) vs too close to now
// (today only) vs open (available).
const getSlotState = (slot, date) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  if (isSlotTooSoon(slot, date)) return "too-soon";
  return "available";
};

const formatDateLabel = (date) => {
  if (date === formatTodayDDMMYYYY()) return "Today";
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

// "review" is display-only in the stepper — it's the checkout/summary
// page this wizard hands off to (handleContinue), not an internal step
// of this component, so `step` state never actually becomes "review".
const STEPS = [
  { key: "category", label: "Category",       sub: "Select category"  },
  { key: "services", label: "Services",       sub: "Choose services"  },
  { key: "schedule", label: "Duration & Time", sub: "Pick date & time" },
  { key: "review",   label: "Review",         sub: "Confirm & pay"    },
];

const ScheduleBookingPage = () => {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const location = useLocation();
  const { cityPrice } = useCityPrice();

  // ── Electrical (technical) catalog — same source as the Instant page ──
  const electricalCatalog = useMemo(() => {
    const types =
      config.SERVICE_CONFIG?.[ELECTRICAL_CATEGORY_KEY]?.services?.[ELECTRICAL_SERVICE_KEY]?.types || [];
    return types.map((service) => ({
      ...service,
      unit: service.unit || "unit",
      limit: service.limit || 10,
      price: service.price || 0,
    }));
  }, []);

  const [selectedElecServices, setSelectedElecServices] = useState([]);

  // Technical services with their own detailRoute (Switch & Socket, CCTV,
  // Appliance, …) run their own standalone wizard — including their own
  // Date & Time step, per-service, rather than the single shared one below —
  // then drop their finished, already-scheduled item into the SAME shared
  // cart (localStorage[STORAGE_KEY]) and navigate back here instead of
  // straight to checkout, so it shows up alongside whatever's being built
  // in selectedElecServices and both get combined into one final checkout.
  // Every item either side writes carries schedule_booking:true +
  // schedule_category_key: "technical" — that's the filter used below.
  const readPendingTechnicalCartItems = () => {
    try {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(all)
        ? all.filter((i) => i?.schedule_booking && i?.schedule_category_key === "technical")
        : [];
    } catch { return []; }
  };
  const [pendingTechnicalCartItems, setPendingTechnicalCartItems] = useState(readPendingTechnicalCartItems);

  useEffect(() => {
    const refresh = () => setPendingTechnicalCartItems(readPendingTechnicalCartItems());
    window.addEventListener("cart-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("cart-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const removePendingTechnicalCartItem = (id) => {
    try {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const updated = (Array.isArray(all) ? all : []).filter((i) => i.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event("cart-updated"));
    } catch { /* ignore */ }
  };

  const pendingTechnicalTotal = useMemo(
    () => pendingTechnicalCartItems.reduce((sum, i) => sum + Number(i.price || 0) * Number(i.qty || 1), 0),
    [pendingTechnicalCartItems]
  );

  // Left-sidebar category picker for the redesigned Services step — which
  // electricalCatalog entry's detail card is shown in the center panel.
  // Defaults to the first catalog entry once it's loaded.
  const [activeElecValue, setActiveElecValue] = useState(null);
  const [socketModalOpen, setSocketModalOpen] = useState(false);
  const [activeSocketService, setActiveSocketService] = useState(null);
  // Generic Work Type → Category → Type drill-down picker (Fan, and any
  // future catalog entry with a `workTypes` shape instead of flat `subServices`).
  const [workTypeModalOpen, setWorkTypeModalOpen] = useState(false);
  const [activeWorkTypeService, setActiveWorkTypeService] = useState(null);
  const [activeWorkTypeKey, setActiveWorkTypeKey] = useState(null);

  // Switch & Socket's own rating → quantity → material popup (see
  // SWITCH_SOCKET_OPTIONS above).
  const [ssModalOpen, setSsModalOpen] = useState(false);
  const [ssOption, setSsOption]       = useState(null);
  // Plug-only lead-in: Vehicle Type then Plug/EV Plug, both asked before
  // anything else. Plain "Plug" then continues into the existing 2-Pin/
  // 3-Pin → Normal/Heavy/Not Sure flow; "EV Plug" skips the 2-Pin/3-Pin
  // step and reuses the same Normal/Heavy/Not Sure Type step directly.
  const [ssVehicleType, setSsVehicleType]     = useState(null); // "2w" | "3w" | "4w" — only asked when ssOption.itemLabel === "Plug"
  const [ssPlugCategory, setSsPlugCategory]   = useState(null); // "plug" | "ev_plug" — only asked when ssOption.itemLabel === "Plug"
  const [ssPlugType, setSsPlugType]   = useState(null); // only asked when ssPlugCategory === "plug"
  const [ssRating, setSsRating]       = useState(null);
  const [ssQty, setSsQty]             = useState(1);
  const [ssMaterial, setSsMaterial]   = useState("clicknido");
  // Real average_rating/review_count per option, from ratings_reviews_col
  // (see seed_ratings_reviews.py) — keyed by SWITCH_SOCKET_OPTIONS' own
  // `key` values. {} until the fetch below resolves.
  const [ssRatingsReviews, setSsRatingsReviews] = useState({});

  // "View details & ratings" — a separate, view-only popup (photo, customer
  // notes/instructions, sample feedback) from the Type/Quantity/Material
  // add-to-cart popup above. Deliberately its own state so both can't fight
  // over the same ssOption.
  const [ssDetailsOpen, setSsDetailsOpen]     = useState(false);
  const [ssDetailsOption, setSsDetailsOption] = useState(null);

  // Appliance's own picker — same "list in center, popup to configure, add
  // to cart" pattern as Switch & Socket, but driven by Appliance's own real
  // data shape (service.appliances[].subServices, each tagged with a
  // workType) instead of ratings/material.
  const [apModalOpen, setApModalOpen]   = useState(false);
  const [apAppliance, setApAppliance]   = useState(null); // the clicked appliances[] entry
  const [apWorkType, setApWorkType]     = useState(null); // "install" | "repair" | "replace" | "remove" | "consultation"
  const [apSubService, setApSubService] = useState(null); // the chosen subServices[] entry
  const [apQty, setApQty]               = useState(1);
  // Only asked when apSubService.isInstall — mirrors the existing
  // "Shown only after picking an install-type sub-service" step already
  // defined on the service (service.installRequirements).
  const [apInstallReq, setApInstallReq] = useState(null);

  // CCTV's own picker — single consolidated card (same collapsed pattern as
  // Stabilizer) whose popup walks through the full "Work Type / Place /
  // Area / CCTV Type / Quantity / Setup / Photo" question set for EVERY
  // work type (not just Install like the old liveView-only version).
  const [ccModalOpen, setCcModalOpen] = useState(false);
  const [ccWorkType, setCcWorkType]   = useState(null); // "install" | "repair" | "replace" | "remove" | "consultation"
  const [ccPlace, setCcPlace]         = useState(null); // service.places[].value
  const [ccArea, setCcArea]           = useState(null); // service.areas[].value
  const [ccType, setCcType]           = useState(null); // the chosen subCategories[0].types[] entry (camera form factor)
  const [ccCount, setCcCount]         = useState(1);     // from cameraCountOptions, or 5+ via the stepper
  const [ccSetup, setCcSetup]         = useState(null); // service.setupOptions[].value
  const [ccPhoto, setCcPhoto]         = useState("");   // optional data URL, display-only (not priced)

  // Doorbell Security's own picker — one card per device SECTION (Regular/
  // Smart/Video Doorbell, Video Door Phone, Security Camera, Security
  // Sensors & Devices), each opening a popup that walks Work Type → Device
  // Type → Material (Install/Replace only) → Existing Connection → Issue
  // (Repair only, optional) → Quantity → Photo (optional) → Notes
  // (optional, required for Consultation) → Add to Cart, per the supplied
  // Doorbell & Security frontend spec.
  const [dbModalOpen, setDbModalOpen]       = useState(false);
  const [dbGroup, setDbGroup]               = useState(null); // the clicked subCategories[] entry (section)
  const [dbWorkType, setDbWorkType]         = useState(null); // "install" | "repair" | "replace" | "remove" | "consultation"
  const [dbDevice, setDbDevice]             = useState(null); // the chosen type within dbGroup
  const [dbMaterial, setDbMaterial]         = useState(null); // only asked for install/replace
  const [dbConnection, setDbConnection]     = useState(null); // asked for every non-consultation work type
  const [dbIssue, setDbIssue]               = useState(null); // only asked (optionally) for repair
  const [dbQty, setDbQty]                   = useState(1);
  const [dbPhoto, setDbPhoto]               = useState("");   // optional data URL, display-only (not priced)
  const [dbNotes, setDbNotes]               = useState("");   // optional free text; required for consultation
  const [dbScopeOpen, setDbScopeOpen]       = useState(false); // collapsible What's Included/Not Included

  // Stabilizer / Submeter & Water Sensor / Switchboard — same device × work
  // type multiplier pattern as CCTV/Doorbell (subCategories[0].types ×
  // workTypes). Switchboard has no top-level consultation object (its own
  // workTypes already include a 5th "Not Sure" entry); Stabilizer/Submeter
  // do, and a device flagged `notSure` routes straight to it, same as
  // Appliance's "no sub-services" shortcut.
  const [stModalOpen, setStModalOpen] = useState(false);
  const [stDevice, setStDevice]       = useState(null);
  const [stWorkType, setStWorkType]   = useState(null);
  const [stQty, setStQty]             = useState(1);

  const [smModalOpen, setSmModalOpen] = useState(false);
  const [smDevice, setSmDevice]       = useState(null);
  const [smWorkType, setSmWorkType]   = useState(null);
  const [smQty, setSmQty]             = useState(1);

  const [sbModalOpen, setSbModalOpen] = useState(false);
  const [sbDevice, setSbDevice]       = useState(null);
  const [sbWorkType, setSbWorkType]   = useState(null);
  const [sbQty, setSbQty]             = useState(1);

  // Wiring — single consolidated card (same collapsed pattern as Stabilizer
  // / CCTV) whose popup walks the full supplied "Customer Booking Process":
  // Work Type (What Do You Need?) → Wiring Type (the only priced dimension)
  // → Purpose (What Is It For?) → Approximate Length → Existing Connection
  // → Quantity → optional Photo. Consultation (from either Step 1 or a
  // "Not Sure" Wiring Type) short-circuits straight to a requirement note.
  const [wrModalOpen, setWrModalOpen]         = useState(false);
  const [wrWorkType, setWrWorkType]           = useState(null); // service.workTypes[].key, or "consultation"
  const [wrType, setWrType]                   = useState(null); // the chosen subCategories[0].types[] entry (priced)
  const [wrPurpose, setWrPurpose]             = useState(null); // service.purposes[].value
  const [wrLengthOption, setWrLengthOption]   = useState(null); // service.lengthOptions[].value
  const [wrConnection, setWrConnection]       = useState(null); // service.connectionOptions[].value
  const [wrQty, setWrQty]                     = useState(1);
  const [wrPhoto, setWrPhoto]                 = useState("");   // optional data URL, display-only (not priced)
  const [wrNotes, setWrNotes]                 = useState("");   // requirement description, required for consultation

  // MCB/Fuse & Light — multi-group pattern (subCategories[], each with its
  // own types[]) × workTypes multiplier, same two-step popup shape as
  // Appliance (group ≈ appliance, chosen type ≈ sub-service) but with an
  // extra work-type-first ordering since every group's types share the
  // same 4 work types (no per-type work-type filtering needed).
  const [mfModalOpen, setMfModalOpen] = useState(false);
  const [mfGroup, setMfGroup]         = useState(null); // the clicked subCategories[] entry
  const [mfWorkType, setMfWorkType]   = useState(null);
  const [mfType, setMfType]           = useState(null); // the chosen type within mfGroup
  const [mfQty, setMfQty]             = useState(1);

  const [ltModalOpen, setLtModalOpen] = useState(false);
  const [ltGroup, setLtGroup]         = useState(null);
  const [ltWorkType, setLtWorkType]   = useState(null);
  const [ltType, setLtType]           = useState(null);
  const [ltQty, setLtQty]             = useState(1);

  // Inverter UPS — components[]/exactWork[] pattern, same shape as
  // Appliance's appliances[]/subServices[] (component ≈ appliance, exact
  // work line ≈ sub-service), except an isInstall line's price is
  // REPLACED by the chosen newInstallationOptions entry instead of having
  // an `extra` amount added on top (Appliance's installRequirements model).
  const [ivModalOpen, setIvModalOpen]     = useState(false);
  const [ivComponent, setIvComponent]     = useState(null);
  const [ivWorkType, setIvWorkType]       = useState(null); // exactWork[].value
  const [ivInstallOpt, setIvInstallOpt]   = useState(null); // newInstallationOptions[].value, only when isInstall
  const [ivQty, setIvQty]                 = useState(1);

  // Fan — 3-level pattern: workTypes[].categories[].types[] (work type →
  // fan category, e.g. "Ceiling Fan" → specific type, e.g. "BLDC Fan",
  // each (workType,category,type) combo carrying its OWN absolute price,
  // no base×multiplier formula) plus a flat `consultation` work type.
  // Center list shows the 5 fan CATEGORIES (shared across all 3 real work
  // types); popup picks Work Type first, then the Type leaf for that
  // category+workType combo.
  const [fnModalOpen, setFnModalOpen] = useState(false);
  const [fnCategory, setFnCategory]   = useState(null); // { label, ...per-workType types lookup } built from the clicked category label
  const [fnWorkType, setFnWorkType]   = useState(null);
  const [fnType, setFnType]           = useState(null);
  const [fnQty, setFnQty]             = useState(1);

  useEffect(() => {
    const ids = SWITCH_SOCKET_OPTIONS.map((o) => o.key).join(",");
    fetch(`${API_BASE_URL}/api/services/ratings-reviews/?ids=${ids}`)
      .then((res) => res.json())
      .then((data) => { if (data?.success) setSsRatingsReviews(data.data || {}); })
      .catch(() => {}); // best-effort — rows just show without a rating line if this fails
  }, []);

  const getSelectedElecService = (value) => selectedElecServices.find((s) => s.value === value);

  // Switch & Socket's price is a flat material-adjusted rate, never run
  // through cityPrice()/the Mysuru auto-discount — this service explicitly
  // uses its own pricing doc, not the site-wide city-price system (see the
  // pricing model comment on the switch-socket entry in pricing.js).
  const resolvedElecPrice = (s) => (s.noCityPrice ? s.price : cityPrice(s.value, s.price));

  const getElecParentSelectedQty = (parentValue) =>
    selectedElecServices
      .filter((s) => s.parentValue === parentValue)
      .reduce((sum, s) => sum + s.quantity, 0);

  const addElecService = (service) => {
    const exists = selectedElecServices.find((s) => s.value === service.value);
    if (exists) {
      setSelectedElecServices((prev) =>
        prev.map((s) =>
          s.value === service.value
            ? { ...s, quantity: Math.min((s.quantity || 1) + 1, service.limit || 10) }
            : s
        )
      );
    } else {
      setSelectedElecServices((prev) => [...prev, { ...service, quantity: 1 }]);
    }
  };

  const updateElecQty = (value, delta, limit) => {
    setSelectedElecServices((prev) =>
      prev
        .map((s) => {
          if (s.value !== value) return s;
          const nextQty = Math.max(0, Math.min((s.quantity || 0) + delta, limit || 10));
          return { ...s, quantity: nextQty };
        })
        .filter((s) => s.quantity > 0)
    );
  };

  const openSocketModal = (service) => {
    setActiveSocketService(service);
    setSocketModalOpen(true);
  };

  const closeSocketModal = () => {
    setSocketModalOpen(false);
    setActiveSocketService(null);
  };

  const addSocketSubService = (parentService, subService) => {
    const parentLimit = parentService.limit || 5;
    const existingQty = getElecParentSelectedQty(parentService.value);
    if (existingQty >= parentLimit) return;

    const existing = selectedElecServices.find((s) => s.value === subService.value);
    if (existing) {
      setSelectedElecServices((prev) =>
        prev.map((s) =>
          s.value === subService.value
            ? { ...s, quantity: Math.min((s.quantity || 0) + 1, subService.limit || parentLimit) }
            : s
        )
      );
    } else {
      setSelectedElecServices((prev) => [
        ...prev,
        {
          ...subService,
          parentValue: parentService.value,
          parentLabel: parentService.label,
          image: parentService.image,
          unit: subService.unit || parentService.unit || "unit",
          limit: subService.limit || parentLimit,
          quantity: 1,
        },
      ]);
    }
  };

  const openWorkTypeModal = (service) => {
    setActiveWorkTypeService(service);
    setActiveWorkTypeKey(service.workTypes?.[0]?.key || null);
    setWorkTypeModalOpen(true);
  };

  const closeWorkTypeModal = () => {
    setWorkTypeModalOpen(false);
    setActiveWorkTypeService(null);
    setActiveWorkTypeKey(null);
  };

  const openSsModal = (option) => {
    setSsOption(option);
    setSsVehicleType(null);
    setSsPlugCategory(null);
    setSsPlugType(null);
    setSsRating(null);
    setSsQty(1);
    setSsMaterial("clicknido");
    setSsModalOpen(true);
  };

  // Just closes the popup — the center panel still shows the 5-option list
  // underneath, so the customer can immediately pick a different one to add
  // more (e.g. Switch Install, close, then Socket Replacement) without
  // losing anything already added.
  const closeSsModal = () => {
    setSsModalOpen(false);
    setSsOption(null);
  };

  const openSsDetailsModal = (option) => {
    setSsDetailsOption(option);
    setSsDetailsOpen(true);
  };

  const closeSsDetailsModal = () => {
    setSsDetailsOpen(false);
    setSsDetailsOption(null);
  };

  // "Add to Cart" shortcut inside the details popup — closes it and opens
  // the real Type/Quantity/Material popup for the same option, so viewing
  // details is never a dead end.
  const switchFromDetailsToAdd = (option) => {
    closeSsDetailsModal();
    openSsModal(option);
  };

  const confirmSsAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "switch-socket");
    if (!service || !ssOption) return;

    if (ssOption.isConsultation) {
      const value = "switchsocket_consultation";
      const limit = 5;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) {
          return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, limit) } : s));
        }
        return [...prev, {
          value, label: "Consultation", price: service.consultationFee || 90,
          quantity: 1, image: ssOption.image || service.image, limit, noCityPrice: true,
          is_consultation: true, sub_service_name: "Switch & Socket",
          booking_type: "consultation", consultation_charge: service.consultationFee || 90,
        }];
      });
      closeSsModal();
      return;
    }

    const isPlug = ssOption.itemLabel === "Plug";
    if (isPlug && !ssVehicleType) return;
    if (isPlug && !ssPlugCategory) return;
    const isEvPlug = isPlug && ssPlugCategory === "ev_plug";
    if (isPlug && !isEvPlug && !ssPlugType) return;
    const vehicleInfo = isPlug ? VEHICLE_TYPES.find((v) => v.value === ssVehicleType) : null;
    const plugTypeInfo = isPlug && !isEvPlug ? PLUG_TYPES.find((p) => p.value === ssPlugType) : null;

    if (!ssRating) return;
    const rating = service.ratings?.find((r) => r.value === ssRating);
    if (!rating) return;

    const materialFactor = ssMaterial === "clicknido" ? 1 : (service.customerMaterialFactor || 0.6);
    // "Not Sure" has no fixed base price — the electrician quotes on-site
    // after inspection, so this adds as a ₹0 placeholder line, clearly
    // labelled, rather than guessing a number.
    const unitPrice = typeof rating.basePrice === "number"
      ? Number((rating.basePrice * materialFactor).toFixed(2))
      : 0;
    const materialLabel = ssMaterial === "clicknido" ? "Company material" : "Customer material";
    const value = `switchsocket_${ssOption.workTypeKey}_${ssOption.itemLabel.toLowerCase()}_${isPlug ? `${ssVehicleType}_${ssPlugCategory}_${isEvPlug ? "" : `${ssPlugType}_`}` : ""}${rating.value}_${ssMaterial}`;
    const workTypeLabelPart = ssOption.workTypeKey === "install" ? "Install" : "Replacement";
    const itemLabelPart = isPlug
      ? `${vehicleInfo?.label} ${isEvPlug ? "EV Plug" : plugTypeInfo?.label} ${workTypeLabelPart}`
      : ssOption.label;
    const label = `${itemLabelPart} — ${rating.label} (${materialLabel})`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + ssQty, service.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: ssQty,
        image: ssOption.image || service.image, limit: service.limit || 10, noCityPrice: true,
        is_consultation: typeof rating.basePrice !== "number", sub_service_name: "Switch & Socket",
        booking_type: ssOption.workTypeKey || "",
      }];
    });
    closeSsModal();
  };

  // ── Appliance picker ────────────────────────────────────────────────────
  // "install_related"/"mounting" are per-appliance alternate spellings of
  // the same install-type bucket (Refrigerator/Microwave-Oven's vaguer
  // "Installation-related work", TV's "Mounting-related work") — kept as
  // their own workType keys (not just a relabeled sub-service) so the
  // Work Type button itself reads right for those appliances, per the
  // supplied "Appliance Possible Service Options" spec.
  const APPLIANCE_WORK_TYPE_LABELS = {
    install: "Install",
    install_related: "Installation-related work",
    mounting: "Mounting-related work",
    replace: "Replace",
    repair: "Repair",
    service: "Service",
    remove: "Uninstall",
    consultation: "Consultation",
  };

  const openApModal = (appliance) => {
    setApAppliance(appliance);
    setApWorkType(null);
    setApSubService(null);
    setApQty(1);
    setApInstallReq(null);
    setApModalOpen(true);
  };

  const closeApModal = () => {
    setApModalOpen(false);
    setApAppliance(null);
  };

  // Work types shown are only the ones this specific appliance actually has
  // sub-services for — not every appliance offers Replace, for instance.
  const apAvailableWorkTypes = (appliance) => {
    if (!appliance) return [];
    const keys = new Set(
      (appliance.subServices || []).map((s) => (s.isConsultation ? "consultation" : s.workType))
    );
    return Object.keys(APPLIANCE_WORK_TYPE_LABELS).filter((k) => keys.has(k));
  };

  // Most work types resolve to exactly one sub-service for a given
  // appliance; "install" sometimes has two (e.g. AC has both "New
  // Installation" and a separate "Wiring & Switch Box" line) — those cases
  // need a second pick, everything else auto-resolves.
  const apSubServicesFor = (appliance, workType) =>
    (appliance?.subServices || []).filter((s) =>
      workType === "consultation" ? s.isConsultation : s.workType === workType && !s.isConsultation
    );

  const pickApWorkType = (workType) => {
    setApWorkType(workType);
    const matches = apSubServicesFor(apAppliance, workType);
    setApSubService(matches.length === 1 ? matches[0] : null);
    setApInstallReq(null);
  };

  const pickApSubService = (sub) => {
    setApSubService(sub);
    setApInstallReq(null);
  };

  const confirmApAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "appliance");
    if (!service || !apAppliance || !apSubService) return;
    if (apSubService.isInstall && !apInstallReq) return;

    const reqInfo = apSubService.isInstall
      ? (service.installRequirements || []).find((r) => r.value === apInstallReq)
      : null;
    const unitPrice = apSubService.price + (reqInfo?.extra || 0);
    const label = `${apAppliance.label} — ${apSubService.label}${reqInfo ? ` (${reqInfo.label})` : ""}`;
    const value = `${apSubService.value}${apInstallReq ? `_${apInstallReq}` : ""}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + apQty, service.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: apQty,
        image: apAppliance.image || service.image, limit: service.limit || 10, noCityPrice: true,
        is_consultation: !!apSubService.isConsultation, sub_service_name: "Appliance",
        booking_type: apSubService.isConsultation ? "consultation" : apWorkType,
        consultation_charge: apSubService.isConsultation ? unitPrice : 0,
      }];
    });
    closeApModal();
  };

  // ── CCTV picker ──────────────────────────────────────────────────────────
  const openCcModal = () => {
    setCcWorkType(null);
    setCcPlace(null);
    setCcArea(null);
    setCcType(null);
    setCcCount(1);
    setCcSetup(null);
    setCcPhoto("");
    setCcModalOpen(true);
  };

  const closeCcModal = () => {
    setCcModalOpen(false);
  };

  const confirmCcAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "cctv");
    if (!service || !ccWorkType) return;

    if (ccWorkType === "consultation") {
      const value = service.consultation?.value || "cctv_consultation";
      const price = service.consultation?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: "CCTV Consultation", price, quantity: 1, image: service.image, limit: 5, noCityPrice: true,
          is_consultation: true, sub_service_name: "CCTV", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeCcModal();
      return;
    }

    if (!ccType || !ccPlace || !ccArea || !ccSetup) return;
    const workType = (service.workTypes || []).find((w) => w.key === ccWorkType);
    if (!workType) return;
    const placeInfo = (service.places || []).find((p) => p.value === ccPlace);
    const areaInfo = (service.areas || []).find((a) => a.value === ccArea);
    const setupInfo = (service.setupOptions || []).find((s) => s.value === ccSetup);
    const unitPrice = Math.round(ccType.price * workType.multiplier);
    const label = `${workType.label} — ${ccType.label} CCTV (${placeInfo?.label}, ${areaInfo?.label}${setupInfo ? `, ${setupInfo.label}` : ""})`;
    const value = `${ccType.value}_${ccWorkType}_${ccPlace}_${ccArea}_${ccSetup}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + ccCount, service.limit || 20) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: ccCount,
        image: ccPhoto || ccType.image || service.image, limit: service.limit || 20, noCityPrice: true,
        is_consultation: !!ccType.notSure, sub_service_name: "CCTV", booking_type: ccWorkType,
        consultation_charge: ccType.notSure ? unitPrice : 0,
      }];
    });
    closeCcModal();
  };

  // ── Doorbell Security picker ────────────────────────────────────────────
  const openDbModal = (group) => {
    setDbGroup(group);
    setDbWorkType(null);
    setDbDevice(null);
    setDbMaterial(null);
    setDbConnection(null);
    setDbIssue(null);
    setDbQty(1);
    setDbPhoto("");
    setDbNotes("");
    setDbModalOpen(true);
  };

  const closeDbModal = () => {
    setDbModalOpen(false);
    setDbGroup(null);
  };

  const confirmDbAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "doorbell-security");
    if (!service || !dbWorkType) return;

    // Consultation, and any "Other / Not Sure" device regardless of work
    // type — pricing can't be determined up front, so both route to the
    // same flat consultation charge ("price after inspection") instead of
    // a ₹0 placeholder line, same convention as every other category.
    if (dbWorkType === "consultation" || dbDevice?.notSure) {
      if (dbWorkType === "consultation" && !dbNotes.trim()) return;
      const value = service.consultation?.value || "doorbell_consultation";
      const price = service.consultation?.price || 99;
      const labelSuffix = dbDevice && !dbDevice.notSure ? ` — ${dbDevice.label}` : "";
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: `Doorbell & Security Consultation${labelSuffix}`, price, quantity: 1,
          image: dbDevice?.image || service.image, limit: 5, noCityPrice: true,
          short: dbNotes.trim() || undefined,
          is_consultation: true, sub_service_name: "Doorbell Security", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeDbModal();
      return;
    }

    if (!dbDevice || !dbConnection) return;
    const needsMaterial = dbWorkType === "install" || dbWorkType === "replace";
    if (needsMaterial && !dbMaterial) return;

    const workType = (service.workTypes || []).find((w) => w.key === dbWorkType);
    if (!workType) return;
    const materialFactor = dbMaterial === "customer" ? (service.customerMaterialFactor || 0.6) : 1;
    const unitPrice = Math.round(dbDevice.price * workType.multiplier * materialFactor);

    const materialInfo = needsMaterial ? (service.materialOptions || []).find((m) => m.value === dbMaterial) : null;
    const connectionInfo = (service.connectionOptions || []).find((c) => c.value === dbConnection);
    const issueInfo = dbWorkType === "repair" ? (service.issueOptions || []).find((i) => i.value === dbIssue) : null;
    const value = `${dbDevice.value}_${dbWorkType}_${dbMaterial || "na"}_${dbConnection}`;
    const detailBits = [materialInfo?.label, connectionInfo?.label, issueInfo?.label].filter(Boolean).join(", ");
    const label = `${dbDevice.label} — ${workType.label}${detailBits ? ` (${detailBits})` : ""}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + dbQty, service.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: dbQty,
        image: dbPhoto || dbDevice.image || service.image, limit: service.limit || 10, noCityPrice: true,
        short: dbNotes.trim() || undefined,
      }];
    });
    closeDbModal();
  };

  // ── Stabilizer picker ────────────────────────────────────────────────────
  // No device argument — the popup now starts on its own "Appliance" step
  // (Home/AC/TV/Fridge/…), picked via setStDevice from inside the modal,
  // instead of being chosen from a per-appliance row in the center list.
  const openStModal = () => {
    setStDevice(null);
    setStWorkType(null);
    setStQty(1);
    setStModalOpen(true);
  };
  const closeStModal = () => {
    setStModalOpen(false);
    setStDevice(null);
  };
  const confirmStAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "stabilizer");
    if (!service || !stDevice) return;

    if (stDevice.notSure || stWorkType === "consultation") {
      const value = service.consultation?.value || "stabilizer_consultation";
      const price = service.consultation?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: "Stabiliser Consultation", price, quantity: 1, image: stDevice.image || service.image, limit: 5, noCityPrice: true,
          is_consultation: true, sub_service_name: "Stabilizer", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeStModal();
      return;
    }

    if (!stWorkType) return;
    const workType = (service.workTypes || []).find((w) => w.key === stWorkType);
    if (!workType) return;
    const unitPrice = Math.round(stDevice.price * workType.multiplier);
    const value = `${stDevice.value}_${stWorkType}`;
    const label = `${stDevice.label} — ${workType.label}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + stQty, service.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: stQty,
        image: stDevice.image || service.image, limit: service.limit || 10, noCityPrice: true,
        startingPrice: stWorkType === "install" || !!stDevice.startingPrice,
      }];
    });
    closeStModal();
  };

  // ── Submeter & Water Sensor picker ──────────────────────────────────────
  const openSmModal = (device) => {
    setSmDevice(device);
    setSmWorkType(null);
    setSmQty(1);
    setSmModalOpen(true);
  };
  const closeSmModal = () => {
    setSmModalOpen(false);
    setSmDevice(null);
  };
  const confirmSmAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "submeter-water-sensor");
    if (!service || !smDevice || !smWorkType) return;

    if (smWorkType === "consultation") {
      const value = service.consultation?.value || "submeter_water_sensor_consultation";
      const price = service.consultation?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: "Submeter & Water Sensor Consultation", price, quantity: 1, image: service.image, limit: 5, noCityPrice: true,
          is_consultation: true, sub_service_name: "Submeter & Water Sensor", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeSmModal();
      return;
    }

    const workType = (service.workTypes || []).find((w) => w.key === smWorkType);
    if (!workType) return;
    const unitPrice = Math.round(smDevice.price * workType.multiplier);
    const value = `${smDevice.value}_${smWorkType}`;
    const label = `${smDevice.label} — ${workType.label}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + smQty, service.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: smQty,
        image: smDevice.image || service.image, limit: service.limit || 10, noCityPrice: true,
        startingPrice: smWorkType === "install",
      }];
    });
    closeSmModal();
  };

  // ── Switchboard picker ───────────────────────────────────────────────────
  const openSbModal = (device) => {
    setSbDevice(device);
    setSbWorkType(null);
    setSbQty(1);
    setSbModalOpen(true);
  };
  const closeSbModal = () => {
    setSbModalOpen(false);
    setSbDevice(null);
  };
  const confirmSbAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "switchboard");
    if (!service || !sbDevice || !sbWorkType) return;

    const workType = (service.workTypes || []).find((w) => w.key === sbWorkType);
    if (!workType) return;
    const unitPrice = Math.round(sbDevice.price * workType.multiplier);
    const value = `${sbDevice.value}_${sbWorkType}`;
    const deviceLabel = ["switchboard_other", "switchboard_notsure"].includes(sbDevice.value)
      ? sbDevice.label
      : `${sbDevice.label}-Point Switchboard`;
    const label = `${deviceLabel} — ${workType.label}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + sbQty, sbDevice.limit || service.limit || 1) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: sbQty,
        image: sbDevice.image || service.image, limit: sbDevice.limit || service.limit || 1, noCityPrice: true,
        startingPrice: sbWorkType === "install" || sbWorkType === "notsure" || !!sbDevice.notSure,
        is_consultation: !!sbDevice.notSure, sub_service_name: "Switchboard", booking_type: sbWorkType,
        consultation_charge: sbDevice.notSure ? unitPrice : 0,
      }];
    });
    closeSbModal();
  };

  // ── Wiring picker ────────────────────────────────────────────────────────
  const openWrModal = () => {
    setWrWorkType(null);
    setWrType(null);
    setWrPurpose(null);
    setWrLengthOption(null);
    setWrConnection(null);
    setWrQty(1);
    setWrPhoto("");
    setWrNotes("");
    setWrModalOpen(true);
  };
  const closeWrModal = () => {
    setWrModalOpen(false);
  };
  const confirmWrAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "wiring");
    if (!service || !wrWorkType) return;

    const isConsultation = wrWorkType === "consultation" || wrType?.notSure;
    if (isConsultation) {
      if (!wrNotes.trim()) return;
      const value = service.consultation?.value || "wiring_consultation";
      const price = service.consultation?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: "Wiring Consultation", price, quantity: 1, image: service.image, limit: 5, noCityPrice: true, short: wrNotes.trim(),
          is_consultation: true, sub_service_name: "Wiring", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeWrModal();
      return;
    }

    if (!wrType || !wrPurpose || !wrLengthOption || !wrConnection) return;
    const workTypeInfo = (service.workTypes || []).find((w) => w.key === wrWorkType);
    const purposeInfo = (service.purposes || []).find((p) => p.value === wrPurpose);
    const lengthInfo = (service.lengthOptions || []).find((l) => l.value === wrLengthOption);
    const connectionInfo = (service.connectionOptions || []).find((c) => c.value === wrConnection);
    const value = `${wrType.value}_${wrWorkType}_${wrPurpose}_${wrLengthOption}_${wrConnection}`;
    const label = `${workTypeInfo?.label} — ${wrType.label} (${purposeInfo?.label}, ${lengthInfo?.label}, ${connectionInfo?.label})`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + wrQty, wrType.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: wrType.price, quantity: wrQty,
        image: wrPhoto || service.image, limit: wrType.limit || 10, noCityPrice: true,
      }];
    });
    closeWrModal();
  };

  // ── MCB/Fuse picker ──────────────────────────────────────────────────────
  const openMfModal = (group) => {
    setMfGroup(group);
    setMfWorkType(null);
    setMfType(null);
    setMfQty(1);
    setMfModalOpen(true);
  };
  const closeMfModal = () => {
    setMfModalOpen(false);
    setMfGroup(null);
  };
  const pickMfWorkType = (key) => {
    setMfWorkType(key);
    setMfType(null);
  };
  const confirmMfAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "mcb-fuse");
    if (!service || !mfGroup) return;

    if ((mfGroup.types || []).length === 0 || mfWorkType === "consultation") {
      const value = service.consultation?.value || "mcb_fuse_consultation";
      const price = service.consultation?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: `${mfGroup.label} — Consultation`, price, quantity: 1, image: service.image, limit: 5, noCityPrice: true,
          is_consultation: true, sub_service_name: "MCB / Fuse", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeMfModal();
      return;
    }

    if (!mfWorkType || !mfType) return;
    const workType = (service.workTypes || []).find((w) => w.key === mfWorkType);
    if (!workType) return;
    const unitPrice = Math.round(mfType.price * workType.multiplier);
    const value = `${mfType.value}_${mfWorkType}`;
    const label = `${mfType.label} — ${workType.label}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + mfQty, mfType.limit || service.limit || 20) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: mfQty,
        image: mfType.image || service.image, limit: mfType.limit || service.limit || 20, noCityPrice: true,
        startingPrice: !!mfType.startingPrice,
      }];
    });
    closeMfModal();
  };

  // ── Light picker ─────────────────────────────────────────────────────────
  const openLtModal = (group) => {
    setLtGroup(group);
    setLtWorkType(null);
    setLtType(null);
    setLtQty(1);
    setLtModalOpen(true);
  };
  const closeLtModal = () => {
    setLtModalOpen(false);
    setLtGroup(null);
  };
  const pickLtWorkType = (key) => {
    setLtWorkType(key);
    setLtType(null);
  };
  const confirmLtAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "light");
    if (!service || !ltGroup) return;

    if ((ltGroup.types || []).length === 0 || ltWorkType === "consultation") {
      const value = service.consultation?.value || "light_consultation";
      const price = service.consultation?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: `${ltGroup.label} Light — Consultation`, price, quantity: 1, image: service.image, limit: 5, noCityPrice: true,
          is_consultation: true, sub_service_name: "Light", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeLtModal();
      return;
    }

    if (!ltWorkType || !ltType) return;
    const workType = (service.workTypes || []).find((w) => w.key === ltWorkType);
    if (!workType) return;
    const unitPrice = Math.round(ltType.price * workType.multiplier);
    const value = `${ltType.value}_${ltWorkType}`;
    const label = `${ltType.label} — ${workType.label}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + ltQty, ltType.limit || service.limit || 20) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: ltQty,
        image: ltType.image || service.image, limit: ltType.limit || service.limit || 20, noCityPrice: true,
        startingPrice: !!ltType.startingPrice,
      }];
    });
    closeLtModal();
  };

  // ── Inverter UPS picker ──────────────────────────────────────────────────
  const openIvModal = (component) => {
    setIvComponent(component);
    setIvWorkType(null);
    setIvInstallOpt(null);
    setIvQty(1);
    setIvModalOpen(true);
  };
  const closeIvModal = () => {
    setIvModalOpen(false);
    setIvComponent(null);
  };
  const pickIvWorkType = (work) => {
    setIvWorkType(work.value);
    setIvInstallOpt(null);
  };
  const confirmIvAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "inverter-ups");
    if (!service || !ivComponent) return;

    if ((ivComponent.exactWork || []).length === 0) {
      const value = service.consultation?.value || "inverter_consultation";
      const price = service.consultation?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: `${ivComponent.label} — Consultation`, price, quantity: 1, image: ivComponent.image || service.image, limit: 5, noCityPrice: true,
          is_consultation: true, sub_service_name: "Inverter UPS", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeIvModal();
      return;
    }

    const work = (ivComponent.exactWork || []).find((w) => w.value === ivWorkType);
    if (!work) return;
    if (work.isInstall && !ivInstallOpt) return;

    const installInfo = work.isInstall ? (service.newInstallationOptions || []).find((o) => o.value === ivInstallOpt) : null;
    const unitPrice = work.isInstall ? (installInfo?.price || 0) : work.price;
    const label = `${ivComponent.label} — ${work.isInstall ? (installInfo?.label || work.label) : work.label}`;
    const value = work.isInstall ? `${work.value}_${ivInstallOpt}` : work.value;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + ivQty, service.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: unitPrice, quantity: ivQty,
        image: ivComponent.image || service.image, limit: service.limit || 10, noCityPrice: true,
      }];
    });
    closeIvModal();
  };

  // ── Fan picker ───────────────────────────────────────────────────────────
  const fnTypesFor = (workTypeKey, categoryLabel) => {
    const service = electricalCatalog.find((s) => s.value === "fan");
    const wt = (service?.workTypes || []).find((w) => w.key === workTypeKey);
    const cat = (wt?.categories || []).find((c) => c.label === categoryLabel);
    return cat?.types || [];
  };
  const openFnModal = (categoryLabel) => {
    setFnCategory(categoryLabel);
    setFnWorkType(null);
    setFnType(null);
    setFnQty(1);
    setFnModalOpen(true);
  };
  const closeFnModal = () => {
    setFnModalOpen(false);
    setFnCategory(null);
  };
  const pickFnWorkType = (key) => {
    setFnWorkType(key);
    setFnType(null);
  };
  const confirmFnAdd = () => {
    const service = electricalCatalog.find((s) => s.value === "fan");
    if (!service || !fnCategory || !fnWorkType) return;

    if (fnWorkType === "consultation") {
      const flat = (service.workTypes || []).find((w) => w.key === "consultation")?.flat;
      const value = flat?.value || "fan_consultation";
      const price = flat?.price || 99;
      setSelectedElecServices((prev) => {
        const exists = prev.find((s) => s.value === value);
        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
        return [...prev, {
          value, label: flat?.label || "Fan Consultation", price, quantity: 1, image: flat?.image || service.image, limit: 5, noCityPrice: true,
          is_consultation: true, sub_service_name: "Fan", booking_type: "consultation", consultation_charge: price,
        }];
      });
      closeFnModal();
      return;
    }

    if (!fnType) return;
    const value = fnType.value;
    const workTypeLabel = { repair: "Repair", install: "Install", remove: "Remove" }[fnWorkType] || fnWorkType;
    const label = `${fnType.label} — ${workTypeLabel}`;

    setSelectedElecServices((prev) => {
      const exists = prev.find((s) => s.value === value);
      if (exists) {
        return prev.map((s) =>
          s.value === value ? { ...s, quantity: Math.min(s.quantity + fnQty, fnType.limit || service.limit || 10) } : s
        );
      }
      return [...prev, {
        value, label, price: fnType.price, quantity: fnQty,
        image: fnType.image || service.image, limit: fnType.limit || service.limit || 10, noCityPrice: true,
      }];
    });
    closeFnModal();
  };

  // Adds a leaf Work Type × Category × Type selection (or the flat
  // Consultation item) — reuses addSocketSubService's generic qty/limit
  // logic, just with a label composed from the drill-down path so it reads
  // clearly in the cart/checkout ("Fan - Repair — Ceiling Fan — BLDC Fan").
  const addWorkTypeLeaf = (parentService, workTypeLabel, categoryLabel, leaf) => {
    const composedLabel = categoryLabel
      ? `${workTypeLabel} — ${categoryLabel} — ${leaf.label}`
      : `${workTypeLabel} — ${leaf.label}`;
    addSocketSubService(parentService, { ...leaf, label: composedLabel });
  };

  const elecSubtotal = useMemo(
    () => selectedElecServices.reduce((sum, s) => sum + resolvedElecPrice(s) * s.quantity, 0),
    [selectedElecServices, cityPrice] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const elecTax = useMemo(() => Number(((elecSubtotal * 18) / 100).toFixed(2)), [elecSubtotal]);
  const elecSurcharge = useMemo(() => Number(((elecSubtotal * 5) / 100).toFixed(2)), [elecSubtotal]);
  const elecGrandTotal = useMemo(
    () => Number((elecSubtotal + elecTax + elecSurcharge).toFixed(2)),
    [elecSubtotal, elecTax, elecSurcharge]
  );
  const elecTotalQty = useMemo(
    () => selectedElecServices.reduce((sum, s) => sum + s.quantity, 0),
    [selectedElecServices]
  );
  const activeElecService = electricalCatalog.find((s) => s.value === activeElecValue) || electricalCatalog[0] || null;

  // Default the sidebar to ?service= from the URL if present (so a direct
  // link/bookmark/browser-back lands back on the same service, e.g. Switch
  // & Socket or Switchboard), else the first catalog entry.
  useEffect(() => {
    if (activeElecValue || electricalCatalog.length === 0) return;
    const urlService = new URLSearchParams(location.search).get("service");
    const match = urlService && electricalCatalog.find((s) => s.value === urlService);
    setActiveElecValue(match ? match.value : electricalCatalog[0].value);
  }, [electricalCatalog, activeElecValue, location.search]);

  // Keeps the URL's ?service= in sync with the sidebar selection — shallow
  // (replace, no real navigation/remount), same approach as the Dashboard's
  // own tab/region URL sync.
  const selectElecService = (value) => {
    setActiveElecValue(value);
    const params = new URLSearchParams(location.search);
    params.set("service", value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError,   setConfigError]   = useState("");
  const [categories,    setCategories]    = useState([]);
  const [services,      setServices]      = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { categories: c, services: s } = await fetchScheduleConfig();
        if (!cancelled) { setCategories(c); setServices(s); }
      } catch (e) {
        if (!cancelled) setConfigError(e?.message || "Failed to load service catalog.");
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [step, setStep]         = useState("category");
  const [category, setCategory] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [duration, setDuration] = useState(null);
  // Local 1hr/2hr toggle for the "What's Covered" preview on the Choose
  // Service step — independent of `duration` above, which is only set
  // once the customer reaches the Duration & Time step.
  const [scopeDuration, setScopeDuration] = useState(1);

  // Home Cleaning's Choose Service step has no selection to validate
  // anymore — Continue unlocks once the customer has scrolled down far
  // enough to see the full 21-item showcase (sentinel div below it
  // entering the viewport), not by ticking any specific services.
  const [homeCleaningReady, setHomeCleaningReady] = useState(false);
  const homeCleaningEndRef = useRef(null);

  useEffect(() => {
    if (step !== "services" || category?.key !== "home_cleaning") return;
    const el = homeCleaningEndRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHomeCleaningReady(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [step, category]);

  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoading]   = useState(false);
  const [availabilityError, setAvailError]  = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const categoryServices = services.filter((s) => s.category === category?.key);
  const selectedServices = categoryServices.filter((s) => selectedIds.includes(s.id));
  const price = category ? (duration === 2 ? category.price_2hr : category.price_1hr) : 0;
  // Only Home Cleaning lets the customer bundle multiple tasks into one
  // visit — Technical and Outdoor are single-service bookings.
  const isMultiSelect = category?.key === "home_cleaning";
  // Technical uses the Instant page's itemized catalog/pricing instead of
  // the generic flat price_1hr/price_2hr model — everything below branches
  // on this flag while leaving Home Cleaning / Outdoor untouched.
  const isElectricalCategory = category?.key === ELECTRICAL_CATEGORY_KEY;

  // push=true (user clicked a category card): adds a history entry at
  // /schedule-booking/<slug> so the URL reflects the pick and back/forward
  // work naturally. push=false (URL-driven pre-select on initial load):
  // state only — we're already at that URL, no navigation needed.
  const chooseCategory = (cat, { push = true } = {}) => {
    setCategory(cat);
    setSelectedIds([]);
    setSelectedElecServices([]);
    setHomeCleaningReady(false);
    setStep("services");
    if (push) navigate(`/schedule-booking/${slugifyCategory(cat.key)}`);
  };

  // Deep-link support: /schedule-booking/home-cleaning (or /technical,
  // /outdoor) jumps straight to that category's Services step once the
  // catalog has loaded, instead of always starting at category selection.
  useEffect(() => {
    if (!categorySlug || category || categories.length === 0) return;
    const key   = unslugifyCategory(categorySlug);
    const match = categories.find((c) => c.key === key);
    if (match) {
      chooseCategory(match, { push: false });
      // Arriving here via "Schedule This Service Instead" after an instant
      // search already failed (InstantOrderTracking.js) — the customer
      // already engaged with service selection once; don't also make them
      // scroll through Home Cleaning's 21-item showcase before Continue
      // unlocks.
      if (location.state?.skipShowcaseGate) setHomeCleaningReady(true);
    }
    // Unknown slug — fall back to the normal category-selection step
    // (already the default), no error needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug, categories]);

  const toggleService = (svc) => {
    if (isMultiSelect) {
      setSelectedIds((prev) =>
        prev.includes(svc.id) ? prev.filter((id) => id !== svc.id) : [...prev, svc.id]
      );
    } else {
      // Single-select: choosing a service replaces any current pick;
      // tapping the selected one again clears it.
      setSelectedIds((prev) => (prev.includes(svc.id) ? [] : [svc.id]));
    }
  };

  // Duration/Date/Time all live on one combined "schedule" step — picking
  // any of the three updates state in place rather than advancing to a
  // new step, so the customer can freely revisit any of them without
  // losing the others.
  const confirmServices = () => {
    // Home Cleaning: no per-service selection anymore (see
    // homeCleaningReady/HOME_CLEANING_SHOWCASE) — the visit covers every
    // backend-listed service at the flat duration price, so Continue just
    // needs the scroll-through gate, then selects all of them for the
    // cart item's service-name list.
    if (category?.key === "home_cleaning") {
      if (!homeCleaningReady) return;
      setSelectedIds(categoryServices.map((s) => s.id));
      setStep("schedule");
      pickDuration(1);
      return;
    }
    if (selectedIds.length === 0) return;
    setStep("schedule");
    pickDuration(1); // default to 1 Hour so all 3 columns are populated immediately
  };

  // Technical: duration doesn't apply to itemized pricing — pickDuration(1)
  // still runs so slot-availability has a value to query with, but no
  // duration UI is shown for this category (see the "schedule" step JSX).
  const confirmElecServices = () => {
    if (selectedElecServices.length === 0) return;
    setStep("schedule");
    pickDuration(1);
  };

  const pickDuration = async (value) => {
    setDuration(value);
    setSelectedDate(""); setSelectedTime(""); setSelectedSlotId("");
    setDays([]); setAvailError(""); setLoading(true);
    try {
      const fetched = await fetchSlotAvailability(category.key, value);
      setDays(fetched);
      const first = fetched.find((d) => d.slots.some((s) => isSlotSelectable(s, d.date)))?.date || fetched[0]?.date || "";
      setSelectedDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoading(false);
    }
  };

  const pickDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(""); setSelectedSlotId("");
  };

  const pickTime = (time, slotId) => {
    setSelectedTime(time);
    setSelectedSlotId(slotId || "");
  };

  const goBack = () => {
    if (step === "services") {
      setStep("category"); setCategory(null); setSelectedIds([]); setSelectedElecServices([]);
      navigate("/schedule-booking");
    }
    if (step === "schedule") {
      setStep("services"); setDuration(null);
      setSelectedDate(""); setSelectedTime(""); setSelectedSlotId(""); setDays([]);
    }
  };

  // "Change" on the schedule step's summary bar — back to Services, keeping
  // the current duration/date/time picks intact (they don't depend on
  // which specific services were chosen, only on category+duration).
  const changeSelection = () => setStep("services");

  const activeDay = days.find((d) => d.date === selectedDate) || null;

  const handleContinue = () => {
    if (!category || !duration || !selectedDate || !selectedTime || selectedServices.length === 0) return;

    const serviceNames = selectedServices.map((s) => s.name);
    const cartItem = {
      id:            `sch-${category.key}-${duration}h-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
      title:         `${category.label} Visit (${duration} Hour)`,
      name:          `${category.label} Visit (${duration} Hour)`,
      short:         serviceNames.join(", "),
      subtitle:      serviceNames.join(", "),
      category:      category.label,
      price,
      image:         getServiceImage(serviceNames[0]),
      qty:           1,
      bookingDate:   selectedDate,
      bookingTime:   selectedTime,
      bookingSlotId: selectedSlotId,
      // Schedule Booking wizard markers — must survive SummaryPage's
      // normalizeItem() so place_order() can record this in the right
      // per-category collection (schedule_home_service / _technical_service
      // / _outdoor_service — see backend note.txt).
      schedule_booking:     true,
      schedule_duration:    duration,
      schedule_category_key: category.key,
    };

    // Mirror the cart's localStorage shape so a page refresh on checkout
    // doesn't lose the selection (SummaryPage falls back to localStorage
    // when navigation state is missing).
    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const withoutSame = Array.isArray(existing) ? existing.filter((i) => i.id !== cartItem.id) : [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, cartItem]));
      window.dispatchEvent(new Event("cart-updated"));
    } catch { /* localStorage unavailable — navigation state below still works */ }

    // Already logged in -> straight to the summary page's final payment
    // button. Not logged in -> login page, which redirects back to the
    // summary page (with this cart item) once they sign in.
    if (isUserLoggedIn()) {
      navigate("/checkout-schedule/summary", { state: { cartItems: [cartItem] } });
    } else {
      navigate("/user/user-login", { state: { redirectTo: "/checkout-schedule/summary", cartItems: [cartItem] } });
    }
  };

  // Technical: one cart item per selected service/sub-service (itemized,
  // same shape the Instant page builds), all stamped with the same
  // date/time slot so place_order() books them together as one visit.
  const handleContinueElectrical = () => {
    if (!category || !selectedDate || !selectedTime || selectedElecServices.length === 0) return;

    const cartItems = selectedElecServices.map((service) => {
      const title = service.parentLabel ? `${service.parentLabel} - ${service.label}` : service.label;
      const subtitle = `${service.quantity} ${service.unit || "unit"}${service.quantity > 1 ? "s" : ""}`;
      return {
        id: `sch-${category.key}-${service.value}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
        title,
        name: title,
        short: subtitle,
        subtitle,
        category: category.label,
        price: resolvedElecPrice(service),
        image: service.image || getServiceImage(title),
        qty: service.quantity,
        bookingDate: selectedDate,
        bookingTime: selectedTime,
        bookingSlotId: selectedSlotId,
        schedule_booking: true,
        schedule_category_key: category.key,
        is_consultation: !!service.is_consultation,
        sub_service_name: service.sub_service_name || service.parentLabel || service.label,
        booking_type: service.booking_type || "",
        consultation_charge: service.consultation_charge || 0,
      };
    });

    // Combine with any already-scheduled items a dedicated technical wizard
    // (Switch & Socket, CCTV, …) dropped into the shared cart earlier in
    // this session — each already carries its own bookingDate/bookingTime,
    // so they're included as-is, not stamped with this step's date/time.
    const combinedCartItems = [...cartItems, ...pendingTechnicalCartItems];

    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const newIds = new Set(combinedCartItems.map((i) => i.id));
      const withoutSame = Array.isArray(existing) ? existing.filter((i) => !newIds.has(i.id)) : [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, ...combinedCartItems]));
      window.dispatchEvent(new Event("cart-updated"));
    } catch { /* localStorage unavailable — navigation state below still works */ }

    if (isUserLoggedIn()) {
      navigate("/checkout-schedule/summary", { state: { cartItems: combinedCartItems } });
    } else {
      navigate("/user/user-login", { state: { redirectTo: "/checkout-schedule/summary", cartItems: combinedCartItems } });
    }
  };

  // When there's nothing to schedule here (customer only added already-
  // scheduled dedicated-wizard items, e.g. just Switch & Socket), skip
  // straight to checkout with those — there's no "this page's" date/time
  // step to run since selectedElecServices is empty.
  const handleCheckoutPendingTechnicalOnly = () => {
    if (pendingTechnicalCartItems.length === 0) return;
    if (isUserLoggedIn()) {
      navigate("/checkout-schedule/summary", { state: { cartItems: pendingTechnicalCartItems } });
    } else {
      navigate("/user/user-login", { state: { redirectTo: "/checkout-schedule/summary", cartItems: pendingTechnicalCartItems } });
    }
  };

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span> <span>Schedule Booking</span>
        </div>

        {step === "category" ? (
          <div style={S.heroSplit}>
            <div style={S.heroLeft}>
              <div style={S.heroBadge}><CalendarDays size={13} /> SCHEDULE BOOKING</div>
              <h1 style={S.heroTitle}>
                Plan Your Service,<br /><span style={S.heroTitleAccent}>We'll Take Care</span>
              </h1>
              <p style={S.heroText}>
                Pick any services you need, choose 1 or 2 hours — one flat price for the visit,
                bookable up to {DAYS_AHEAD} days in advance.
              </p>
            </div>
            <div style={S.heroRight}>
              <img
                src={DEFAULT_HERO_IMAGE}
                alt="Professional home service"
                style={S.heroImg}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div style={S.heroBadgeCard}>
                <div style={S.heroBadgeCardIcon}><CalendarCheck2 size={20} color="#16a34a" /></div>
                <div>
                  <div style={S.heroBadgeCardSub}>Book up to</div>
                  <div style={S.heroBadgeCardMain}>{DAYS_AHEAD} Days</div>
                  <div style={S.heroBadgeCardSub}>in advance</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={S.hero}>
            <div style={S.heroBadge}><CalendarDays size={13} /> SCHEDULE BOOKING</div>
            <h1 style={S.heroTitle}>Plan Your Service, We'll Take Care</h1>
          </div>
        )}

        {loadingConfig ? (
          <div style={S.stateBox}>Loading service catalog…</div>
        ) : configError ? (
          <div style={S.errorBox}>{configError}</div>
        ) : (
          <>
            {/* Step progress */}
            <div style={S.stepRow}>
              {STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div style={S.stepItem}>
                    <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                      {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                    </div>
                    <div>
                      <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                      <div style={S.stepSub}>{s.sub}</div>
                    </div>
                  </div>
                  {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
                </React.Fragment>
              ))}
            </div>

            {step !== "category" && (
              <button type="button" style={S.backBtn} onClick={goBack}>
                <ArrowLeft size={15} /> {step === "services" ? "Back to Categories" : "Back"}
              </button>
            )}

            {/* ── Step 1: Category ─────────────────────────────────────── */}
            {step === "category" && (
              <>
                <h2 style={S.sectionTitle}>Choose a Category</h2>
                <div style={S.categoryGrid}>
                  {categories.map((cat, i) => (
                    <div key={cat.key} style={S.categoryCard}>
                      <img
                        src={getCategoryHeroImage(cat.key)}
                        alt={cat.label}
                        style={S.categoryImg}
                        onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                      />
                      <div style={S.scheduleBadge}><CalendarDays size={11} /> Schedule</div>
                      <div style={S.categoryBody}>
                        <div style={S.categoryLabel}>{cat.label}</div>
                        {cat.key === ELECTRICAL_CATEGORY_KEY ? (
                          <div style={S.categoryPriceRow}>
                            <span style={S.categoryPricePill}>Itemized Pricing</span>
                          </div>
                        ) : (
                          <div style={S.categoryPriceRow}>
                            <span style={S.categoryPricePill}>1 Hr · {fmt(cat.price_1hr)}</span>
                            <span style={S.categoryPricePill}>2 Hr · {fmt(cat.price_2hr)}</span>
                          </div>
                        )}
                        <div style={S.categoryCount}>
                          {cat.key === ELECTRICAL_CATEGORY_KEY
                            ? electricalCatalog.length
                            : services.filter((s) => s.category === cat.key).length} Service{(cat.key === ELECTRICAL_CATEGORY_KEY ? electricalCatalog.length : services.filter((s) => s.category === cat.key).length) === 1 ? "" : "s"} Available
                        </div>
                        <button
                          type="button"
                          style={i === 0 ? S.categoryCtaSolid : S.categoryCtaOutline}
                          onClick={() => chooseCategory(cat)}
                        >
                          Explore Services <ArrowRight size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={S.trustStrip}>
                  <div style={S.trustItem}>
                    <div style={S.trustIconWrap}><CalendarCheck2 size={20} color="#16a34a" /></div>
                    <div>
                      <div style={S.trustTitle}>Book in Advance</div>
                      <div style={S.trustText}>Schedule up to {DAYS_AHEAD} days before.</div>
                    </div>
                  </div>
                  <div style={S.trustItem}>
                    <div style={S.trustIconWrap}><Clock3 size={20} color="#16a34a" /></div>
                    <div>
                      <div style={S.trustTitle}>Flexible Duration</div>
                      <div style={S.trustText}>Choose 1 or 2 hours as per your need.</div>
                    </div>
                  </div>
                  <div style={S.trustItem}>
                    <div style={S.trustIconWrap}><IndianRupee size={20} color="#16a34a" /></div>
                    <div>
                      <div style={S.trustTitle}>Flat Pricing</div>
                      <div style={S.trustText}>One flat price for all services.</div>
                    </div>
                  </div>
                  <div style={S.trustItem}>
                    <div style={S.trustIconWrap}><ShieldCheck size={20} color="#16a34a" /></div>
                    <div>
                      <div style={S.trustTitle}>Trusted Professionals</div>
                      <div style={S.trustText}>Background-checked &amp; verified experts.</div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── Step 2: Services ──────────────────────────────────────── */}
            {/* Home Cleaning: multi-select, one flat price for the visit.
                Technical / Outdoor: single-service booking. */}
            {step === "services" && category && (
              <>
                <div style={S.serviceBanner}>
                  <div style={S.serviceBannerLeft}>
                    <div style={S.serviceBannerIconWrap}><Home size={22} color="#fff" /></div>
                    <div>
                      <div style={S.serviceBannerTitle}>{category.label} Services</div>
                      <div style={S.serviceBannerText}>
                        {isElectricalCategory
                          ? "Select one or more services and quantities — priced per item, same as Instant booking."
                          : isMultiSelect
                          ? "Select any service you need. Price is flat per visit duration, not per service."
                          : "Choose one service for this visit — price is flat per visit duration."}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Home Cleaning only — what's included/excluded for the
                    1-Hour vs 2-Hour schedule, previewed here (before the
                    customer even picks services) via its own toggle —
                    independent of the actual duration choice, which still
                    happens later on the Duration & Time step. */}
                {category.key === "home_cleaning" && HOME_CLEANING_SCOPE[scopeDuration] && (
                  <div style={S.scopeSection}>
                    <div style={S.scopeHead}>
                      <div style={S.scopeHeadLeft}>
                        <Info size={16} color="#4f46e5" />
                        <div>
                          <div style={S.scopeTitle}>What's Covered</div>
                          <div style={S.scopeSub}>{HOME_CLEANING_SCOPE[scopeDuration].fixedDuration}</div>
                        </div>
                      </div>
                      <div style={S.scopeToggle}>
                        {DURATIONS.map((d) => (
                          <button
                            key={d.value}
                            type="button"
                            style={{ ...S.scopeToggleBtn, ...(scopeDuration === d.value ? S.scopeToggleBtnActive : {}) }}
                            onClick={() => setScopeDuration(d.value)}
                          >
                            {d.title}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={S.scopeGrid}>
                      <div style={S.scopePanel}>
                        <div style={S.scopePanelLabel}><CheckCircle2 size={14} color="#16a34a" /> Included Services</div>
                        <ul style={S.scopeList}>
                          {HOME_CLEANING_SCOPE[scopeDuration].included.map((item) => (
                            <li key={item} style={S.scopeListItem}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div style={S.scopePanel}>
                        <div style={S.scopePanelLabel}><XCircle size={14} color="#dc2626" /> Excluded Services</div>
                        <ul style={S.scopeList}>
                          {HOME_CLEANING_SCOPE[scopeDuration].excluded.map((item) => (
                            <li key={item} style={S.scopeListItem}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div style={S.scopeRoutineNote}>
                      <strong>Daily Routine:</strong> {HOME_CLEANING_SCOPE[scopeDuration].dailyRoutine}
                    </div>

                    <div style={S.scopeNotesPanel}>
                      <div style={S.scopePanelLabel}>📝 Customer Notes</div>
                      <div style={S.scopeNotesTable}>
                        {HOME_CLEANING_SCOPE[scopeDuration].customerNotes.map((note) => (
                          <div key={note.label} style={S.scopeNoteRow}>
                            <span style={S.scopeNoteLabel}>{note.label}</span>
                            <span style={S.scopeNoteText}>{note.text}</span>
                          </div>
                        ))}
                      </div>
                      <div style={S.scopeCommonNote}>{HOME_CLEANING_SCOPE[scopeDuration].commonNote}</div>
                    </div>
                  </div>
                )}

                {isElectricalCategory ? (
                  <div style={S.elecLayout} className="cn-elec-layout">
                    {/* Responsive handling for the 3-column Technical Services
                        layout (260px sidebar / 1fr services / 320px cart) —
                        inline styles alone can't express media queries, so
                        this follows the same <style> + className + !important
                        pattern already used elsewhere in the app (e.g.
                        HomePageContent.js's membership grid). */}
                    <style>{`
                      @media (max-width: 1199px) {
                        .cn-elec-layout { grid-template-columns: 1fr !important; }
                        .cn-elec-sidebar { flex-direction: row !important; overflow-x: auto !important; gap: 8px !important; padding: 8px !important; }
                        .cn-elec-sidebar-item { flex: 0 0 auto !important; width: auto !important; white-space: nowrap; }
                        .cn-elec-cart-panel { position: static !important; }
                      }
                      @media (max-width: 767px) {
                        .cn-elec-layout { padding-bottom: 72px; }
                        .cn-elec-detail-card { flex-direction: column !important; align-items: flex-start !important; }
                        .cn-elec-detail-action { min-width: 0 !important; width: 100%; text-align: left !important; }
                        .cn-ss-option-row { flex-direction: column !important; }
                        .cn-ss-option-row img { width: 100% !important; height: 160px !important; }
                        .cn-elec-mobile-bar { display: flex !important; }
                      }
                    `}</style>

                    {/* ── Left: category sidebar ─────────────────────────── */}
                    <div style={S.elecSidebar} className="cn-elec-sidebar">
                      {electricalCatalog.map((svc) => {
                        const svcIsGroup = Array.isArray(svc.subServices) && svc.subServices.length > 0;
                        const svcIsWorkTypeGroup = Array.isArray(svc.workTypes) && svc.workTypes.length > 0;
                        const svcQty = (svcIsGroup || svcIsWorkTypeGroup)
                          ? getElecParentSelectedQty(svc.value)
                          : getSelectedElecService(svc.value)?.quantity || 0;
                        const active = activeElecValue === svc.value;
                        return (
                          <button
                            key={svc.value}
                            type="button"
                            className="cn-elec-sidebar-item"
                            style={{ ...S.elecSidebarItem, ...(active ? S.elecSidebarItemActive : {}) }}
                            onClick={() => selectElecService(svc.value)}
                          >
                            <img
                              src={svc.image}
                              alt=""
                              style={S.elecSidebarIcon}
                              onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                            />
                            <span style={S.elecSidebarLabel}>{svc.label}</span>
                            {svcQty > 0 && <span style={S.elecSidebarBadge}>{svcQty}</span>}
                            <ArrowRight size={14} color={active ? "#16a34a" : "#c7cbd6"} />
                          </button>
                        );
                      })}
                    </div>

                    {/* ── Center: selected category's bookable service ──────── */}
                    <div style={S.elecMain}>
                      {activeElecService && (() => {
                        const service = activeElecService;
                        const isSwitchSocket = service.value === "switch-socket";
                        const isAppliance = service.value === "appliance";
                        const isCctv = service.value === "cctv";
                        const isDoorbell = service.value === "doorbell-security";
                        const isStabilizer = service.value === "stabilizer";
                        const isSubmeter = service.value === "submeter-water-sensor";
                        const isSwitchboard = service.value === "switchboard";
                        const isWiring = service.value === "wiring";
                        const isMcbFuse = service.value === "mcb-fuse";
                        const isLight = service.value === "light";
                        const isInverterUps = service.value === "inverter-ups";
                        const isFan = service.value === "fan";
                        const isGroup = Array.isArray(service.subServices) && service.subServices.length > 0;
                        const isWorkTypeGroup = Array.isArray(service.workTypes) && service.workTypes.length > 0;
                        const isPlaceholder = !isGroup && !isWorkTypeGroup && !service.price;
                        const qty = (isGroup || isWorkTypeGroup)
                          ? getElecParentSelectedQty(service.value)
                          : getSelectedElecService(service.value)?.quantity || 0;
                        const reachedMax = qty >= (service.limit || 10);
                        const description = service.includes?.[0] || `Professional ${service.label} services.`;

                        const handlePrimaryAction = () => {
                          if (isPlaceholder || reachedMax) return;
                          if (isWorkTypeGroup) {
                            service.detailRoute ? navigate(service.detailRoute) : openWorkTypeModal(service);
                          } else if (isGroup) {
                            openSocketModal(service);
                          } else {
                            addElecService(service);
                          }
                        };

                        // Switch & Socket: 5 flat choices (Switch/Socket ×
                        // Install/Replace + Consultation) instead of the
                        // generic single detail card — each opens the
                        // rating/quantity/material popup below.
                        if (isSwitchSocket) {
                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionList} className="cn-ss-option-list">
                                {(() => {
                                  const normalRating = (service.ratings || []).find((r) => r.value === "normal");
                                  return SWITCH_SOCKET_OPTIONS.map((option) => {
                                    // Total across every variant added for this option (any
                                    // rating/material picked in the popup), not just one —
                                    // the popup is the only way to add now, so a customer
                                    // could have Heavy + Customer-material items too.
                                    const qty = selectedElecServices
                                      .filter((s) => s.value.startsWith(
                                        option.isConsultation ? "switchsocket_consultation" : `switchsocket_${option.workTypeKey}_${option.itemLabel.toLowerCase()}_`
                                      ))
                                      .reduce((sum, s) => sum + s.quantity, 0);
                                    const unitPrice = option.isConsultation
                                      ? (service.consultationFee || 90)
                                      : (normalRating?.basePrice ?? 0);
                                    const ratingInfo = ssRatingsReviews[option.key];

                                    return (
                                      <div key={option.key} style={S.ssOptionRow} className="cn-ss-option-row">
                                        <img
                                          src={option.image || service.image}
                                          alt=""
                                          style={S.ssOptionImg}
                                          onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                        />
                                        <div style={S.ssOptionText}>
                                          <div style={S.ssOptionLabel}>{option.label}</div>
                                          {ratingInfo && (
                                            <div style={S.ssOptionRating}>
                                              ⭐ {ratingInfo.average_rating.toFixed(2)} ({formatReviewCount(ratingInfo.review_count)} reviews)
                                            </div>
                                          )}
                                          <div style={S.ssOptionDesc}>{option.description}</div>
                                          <div style={S.ssOptionChipRow}>
                                            <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 15–30 min</span>
                                            <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Installation</span>
                                          </div>
                                          <button
                                            type="button"
                                            style={S.ssOptionDetailsLink}
                                            onClick={() => openSsDetailsModal(option)}
                                          >
                                            {option.isConsultation ? "View details" : "View details & ratings"} ›
                                          </button>
                                        </div>
                                        <div style={S.ssOptionRight}>
                                          <div style={S.ssOptionPriceValue}>₹{unitPrice}</div>
                                          <div style={S.ssOptionPriceUnit}>per item</div>
                                          <div style={S.ssOptionQtyRow}>
                                            {/* Both + and − open the popup — picking a rating/
                                                material is required before anything is added or
                                                changed, there's no silent direct add any more. */}
                                            <button
                                              type="button"
                                              style={qty === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                              onClick={() => qty > 0 && openSsModal(option)}
                                              disabled={qty === 0}
                                              aria-label={`Edit ${option.label} quantity`}
                                            >
                                              <Minus size={13} />
                                            </button>
                                            <span style={S.qtyValueText}>{qty}</span>
                                            <button
                                              type="button"
                                              style={S.elecQtyBtnP}
                                              onClick={() => openSsModal(option)}
                                              aria-label={`Add ${option.label}`}
                                            >
                                              <Plus size={13} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </>
                          );
                        }

                        // Appliance: the real per-appliance list (AC, Geyser,
                        // Chimney, …) instead of the generic single detail
                        // card — each opens the work-type popup below.
                        if (isAppliance) {
                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionList} className="cn-ss-option-list">
                                {(service.appliances || []).map((appliance) => {
                                  const qtyForAppliance = selectedElecServices
                                    .filter((s) => (appliance.subServices || []).some((sub) => s.value.startsWith(sub.value)))
                                    .reduce((sum, s) => sum + s.quantity, 0);
                                  const prices = (appliance.subServices || []).map((s) => s.price).filter((p) => typeof p === "number");
                                  const startingPrice = prices.length ? Math.min(...prices) : null;

                                  return (
                                    <div key={appliance.key} style={S.ssOptionRow} className="cn-ss-option-row">
                                      <img
                                        src={appliance.image || service.image}
                                        alt=""
                                        style={S.ssOptionImg}
                                        onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                      />
                                      <div style={S.ssOptionText}>
                                        <div style={S.ssOptionLabel}>{appliance.label}</div>
                                        <div style={S.ssOptionDesc}>
                                          {appliance.subServices.length > 0
                                            ? "Installation, repair, replacement and removal."
                                            : "Not sure what's wrong? Book a consultation."}
                                        </div>
                                        <div style={S.ssOptionChipRow}>
                                          <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 15–45 min</span>
                                          <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Installation</span>
                                        </div>
                                      </div>
                                      <div style={S.ssOptionRight}>
                                        <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                        <div style={S.ssOptionPriceUnit}>{startingPrice != null ? "starting" : "consultation"}</div>
                                        <div style={S.ssOptionQtyRow}>
                                          <button
                                            type="button"
                                            style={qtyForAppliance === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                            onClick={() => qtyForAppliance > 0 && openApModal(appliance)}
                                            disabled={qtyForAppliance === 0}
                                            aria-label={`Edit ${appliance.label} quantity`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <span style={S.qtyValueText}>{qtyForAppliance}</span>
                                          <button
                                            type="button"
                                            style={S.elecQtyBtnP}
                                            onClick={() => openApModal(appliance)}
                                            aria-label={`Add ${appliance.label}`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          );
                        }

                        // CCTV: the real system-type list (Camera, Home CCTV,
                        // Office CCTV, …) instead of the generic single detail
                        // card — each opens the work-type popup below.
                        // CCTV: a single consolidated card (same collapsed
                        // pattern as Stabilizer) instead of one row per
                        // camera type — Work Type/Place/Area/CCTV Type/
                        // Quantity/Setup/Photo are all picked inside the
                        // popup now, applying equally to every work type.
                        if (isCctv) {
                          const ccTypes = service.subCategories?.[0]?.types || [];
                          const startingPrice = ccTypes.length ? Math.min(...ccTypes.map((t) => t.price)) : null;
                          const totalQty = selectedElecServices
                            .filter((s) => (ccTypes || []).some((t) => s.value.startsWith(`${t.value}_`)))
                            .reduce((sum, s) => sum + s.quantity, 0);

                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionRow} className="cn-ss-option-row">
                                <img
                                  src={service.image}
                                  alt=""
                                  style={S.ssOptionImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div style={S.ssOptionText}>
                                  <div style={S.ssOptionLabel}>CCTV</div>
                                  <div style={S.ssOptionDesc}>
                                    Installation, repair, replacement and removal — indoor, outdoor, Wi-Fi, IP, dome, bullet, PTZ, turret, 4G and smart cameras.
                                  </div>
                                  <div style={S.ssOptionChipRow}>
                                    <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 30–60 min</span>
                                    <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Installation</span>
                                  </div>
                                </div>
                                <div style={S.ssOptionRight}>
                                  <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                  <div style={S.ssOptionPriceUnit}>starting</div>
                                  <button
                                    type="button"
                                    style={{ ...S.ssAddBtn, padding: "11px 18px", whiteSpace: "nowrap" }}
                                    onClick={() => openCcModal()}
                                  >
                                    {totalQty > 0 ? `${totalQty} Added — Add More` : "Select Service"}
                                  </button>
                                </div>
                              </div>
                            </>
                          );
                        }

                        // Doorbell Security: the real device-type list
                        // (Doorbell, Video Doorbell, Wireless, …) instead of
                        // the generic single detail card — each opens the
                        // work-type popup below.
                        // Doorbell & Security: one card per device SECTION
                        // (Regular/Smart/Video Doorbell, Video Door Phone,
                        // Security Camera, Security Sensors & Devices)
                        // instead of every device flattened onto the page —
                        // each opens the full Work Type → Device → Material
                        // → Connection → Issue → Quantity → Photo → Notes
                        // popup below. Includes a collapsible What's
                        // Included / Not Included disclosure.
                        if (isDoorbell) {
                          const dbGroups = service.subCategories || [];
                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>Doorbell &amp; Security Services</div>
                                  <div style={S.elecMainSub}>
                                    Installation, repair, replacement and removal of doorbells and home security devices.
                                  </div>
                                </div>
                              </div>

                              {(service.includes?.length > 0 || service.excludes?.length > 0) && (
                                <div style={{ marginBottom: "14px" }}>
                                  <button
                                    type="button"
                                    style={S.ssOptionDetailsLink}
                                    onClick={() => setDbScopeOpen((v) => !v)}
                                  >
                                    {dbScopeOpen ? "▾" : "▸"} What's Included / Not Included
                                  </button>
                                  {dbScopeOpen && (
                                    <ul style={S.ssNotesList}>
                                      {(service.includes || []).map((line, i) => (
                                        <li key={`inc-${i}`} style={S.ssNotesItemIncluded}><CheckCircle2 size={13} color="#16a34a" /> {line}</li>
                                      ))}
                                      {(service.excludes || []).map((line, i) => (
                                        <li key={`exc-${i}`} style={S.ssNotesItemExcluded}><XCircle size={13} color="#dc2626" /> {line}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}

                              <div style={S.ssOptionList} className="cn-ss-option-list">
                                {dbGroups.map((group) => {
                                  const groupValues = (group.types || []).map((t) => t.value);
                                  const qtyForGroup = selectedElecServices
                                    .filter((s) => groupValues.some((v) => s.value.startsWith(`${v}_`)))
                                    .reduce((sum, s) => sum + s.quantity, 0);
                                  const prices = (group.types || []).map((t) => t.price).filter((p) => typeof p === "number");
                                  const startingPrice = prices.length ? Math.min(...prices) : null;

                                  return (
                                    <div key={group.key} style={S.ssOptionRow} className="cn-ss-option-row">
                                      <img
                                        src={group.types?.[0]?.image || service.image}
                                        alt=""
                                        style={S.ssOptionImg}
                                        onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                      />
                                      <div style={S.ssOptionText}>
                                        <div style={S.ssOptionLabel}>{group.label}</div>
                                        <div style={S.ssOptionDesc}>Installation, repair, replacement and removal.</div>
                                        <div style={S.ssOptionChipRow}>
                                          <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 20–40 min</span>
                                          <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Installation</span>
                                        </div>
                                      </div>
                                      <div style={S.ssOptionRight}>
                                        <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                        <div style={S.ssOptionPriceUnit}>{startingPrice != null ? "starting" : "after inspection"}</div>
                                        <div style={S.ssOptionQtyRow}>
                                          <button
                                            type="button"
                                            style={qtyForGroup === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                            onClick={() => qtyForGroup > 0 && openDbModal(group)}
                                            disabled={qtyForGroup === 0}
                                            aria-label={`Edit ${group.label} quantity`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <span style={S.qtyValueText}>{qtyForGroup}</span>
                                          <button
                                            type="button"
                                            style={S.elecQtyBtnP}
                                            onClick={() => openDbModal(group)}
                                            aria-label={`Add ${group.label}`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          );
                        }

                        // Stabilizer / Submeter & Water Sensor / Switchboard:
                        // the real appliance/type/points list instead of the
                        // generic single detail card — each opens its own
                        // work-type popup below. Same row shape as Doorbell.
                        // Stabilizer: a single consolidated card instead of
                        // one row per appliance type — the appliance picker
                        // moved inside the popup itself (Appliance → Work
                        // Type → Quantity), per explicit request to collapse
                        // the 7-row list down to one entry point.
                        if (isStabilizer) {
                          const devices = service.subCategories?.[0]?.types || [];
                          const startingPrice = devices.length ? Math.min(...devices.map((d) => d.price)) : null;
                          const totalQty = selectedElecServices
                            .filter((s) => s.value.startsWith("stabilizer_"))
                            .reduce((sum, s) => sum + s.quantity, 0);

                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionRow} className="cn-ss-option-row">
                                <img
                                  src={service.image}
                                  alt=""
                                  style={S.ssOptionImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div style={S.ssOptionText}>
                                  <div style={S.ssOptionLabel}>Stabilizer</div>
                                  <div style={S.ssOptionDesc}>
                                    Repair, replace, install or remove — for Home, AC, TV, Fridge, general Appliance or Commercial stabilisers.
                                  </div>
                                  <div style={S.ssOptionChipRow}>
                                    <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 20–40 min</span>
                                    <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Service</span>
                                  </div>
                                </div>
                                <div style={S.ssOptionRight}>
                                  <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                  <div style={S.ssOptionPriceUnit}>starting</div>
                                  <button
                                    type="button"
                                    style={{ ...S.ssAddBtn, padding: "11px 18px", whiteSpace: "nowrap" }}
                                    onClick={() => openStModal()}
                                  >
                                    {totalQty > 0 ? `${totalQty} Added — Add More` : "Select Service"}
                                  </button>
                                </div>
                              </div>
                            </>
                          );
                        }

                        if (isSubmeter || isSwitchboard) {
                          const devices = service.subCategories?.[0]?.types || [];
                          const openModal = isSubmeter ? openSmModal : openSbModal;
                          const rowLabel = (device) =>
                            isSwitchboard && !["switchboard_other", "switchboard_notsure"].includes(device.value)
                              ? `${device.label}-Point Switchboard`
                              : device.label;
                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionList} className="cn-ss-option-list">
                                {devices.map((device) => {
                                  const qtyForDevice = selectedElecServices
                                    .filter((s) => s.value.startsWith(`${device.value}_`))
                                    .reduce((sum, s) => sum + s.quantity, 0);

                                  return (
                                    <div key={device.value} style={S.ssOptionRow} className="cn-ss-option-row">
                                      <img
                                        src={device.image || service.image}
                                        alt=""
                                        style={S.ssOptionImg}
                                        onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                      />
                                      <div style={S.ssOptionText}>
                                        <div style={S.ssOptionLabel}>{rowLabel(device)}</div>
                                        <div style={S.ssOptionDesc}>
                                          {device.notSure ? "Not sure what's needed? Book a consultation." : "Repair, replace, install or remove."}
                                        </div>
                                        <div style={S.ssOptionChipRow}>
                                          <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 20–40 min</span>
                                          <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Service</span>
                                        </div>
                                      </div>
                                      <div style={S.ssOptionRight}>
                                        <div style={S.ssOptionPriceValue}>₹{device.price}</div>
                                        <div style={S.ssOptionPriceUnit}>{device.notSure ? "consultation" : "starting"}</div>
                                        <div style={S.ssOptionQtyRow}>
                                          <button
                                            type="button"
                                            style={qtyForDevice === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                            onClick={() => qtyForDevice > 0 && openModal(device)}
                                            disabled={qtyForDevice === 0}
                                            aria-label={`Edit ${rowLabel(device)} quantity`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <span style={S.qtyValueText}>{qtyForDevice}</span>
                                          <button
                                            type="button"
                                            style={S.elecQtyBtnP}
                                            onClick={() => openModal(device)}
                                            aria-label={`Add ${rowLabel(device)}`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          );
                        }

                        // Wiring: Length options (6m/10m/20m/30m/More/Not
                        // Sure) in the center list — each opens the Wiring
                        // Type (New/Old) popup below.
                        // Wiring: flat job-type list (External/Internal/New
                        // Point/Extension/Repair/Consultation) — clicking +
                        // opens a popup with just Quantity, no Type step.
                        // Wiring: a single consolidated card (same collapsed
                        // pattern as Stabilizer/CCTV) — the full "What Do
                        // You Need? → Wiring Type → Purpose → Approximate
                        // Length → Existing Connection" flow moved inside
                        // the popup itself, per the supplied Customer
                        // Booking Process spec.
                        if (isWiring) {
                          const types = service.subCategories?.[0]?.types || [];
                          const startingPrice = types.length
                            ? Math.min(...types.filter((t) => !t.notSure).map((t) => t.price))
                            : null;
                          const totalQty = selectedElecServices
                            .filter((s) => s.value.startsWith("wiring_"))
                            .reduce((sum, s) => sum + s.quantity, 0);

                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionRow} className="cn-ss-option-row">
                                <img
                                  src={service.image}
                                  alt=""
                                  style={S.ssOptionImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div style={S.ssOptionText}>
                                  <div style={S.ssOptionLabel}>Wiring</div>
                                  <div style={S.ssOptionDesc}>
                                    External, internal, new point, extension or repair wiring — the electrician verifies the exact
                                    requirement on-site.
                                  </div>
                                  <div style={S.ssOptionChipRow}>
                                    <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 30–60 min</span>
                                    <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Service</span>
                                  </div>
                                </div>
                                <div style={S.ssOptionRight}>
                                  <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                  <div style={S.ssOptionPriceUnit}>starting</div>
                                  <button
                                    type="button"
                                    style={{ ...S.ssAddBtn, padding: "11px 18px", whiteSpace: "nowrap" }}
                                    onClick={() => openWrModal()}
                                  >
                                    {totalQty > 0 ? `${totalQty} Added — Add More` : "Select Service"}
                                  </button>
                                </div>
                              </div>
                            </>
                          );
                        }

                        // MCB/Fuse & Light: the real group list (MCB, Fuse,
                        // MCB Box / Normal, LED, Ceiling, …) instead of the
                        // generic single detail card — each opens the
                        // work-type → type popup below.
                        if (isMcbFuse || isLight) {
                          const groups = service.subCategories || [];
                          const openModal = isMcbFuse ? openMfModal : openLtModal;
                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionList} className="cn-ss-option-list">
                                {groups.map((group) => {
                                  const groupValues = (group.types || []).map((t) => t.value);
                                  const qtyForGroup = selectedElecServices
                                    .filter((s) => groupValues.some((v) => s.value.startsWith(`${v}_`)))
                                    .reduce((sum, s) => sum + s.quantity, 0);
                                  const prices = (group.types || []).map((t) => t.price).filter((p) => typeof p === "number");
                                  const startingPrice = prices.length ? Math.min(...prices) : null;

                                  return (
                                    <div key={group.key} style={S.ssOptionRow} className="cn-ss-option-row">
                                      <img
                                        src={group.types?.[0]?.image || service.image}
                                        alt=""
                                        style={S.ssOptionImg}
                                        onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                      />
                                      <div style={S.ssOptionText}>
                                        <div style={S.ssOptionLabel}>{group.label}</div>
                                        <div style={S.ssOptionDesc}>
                                          {(group.types || []).length > 0
                                            ? "Repair, replace, install or remove."
                                            : "Not sure what's wrong? Book a consultation."}
                                        </div>
                                        <div style={S.ssOptionChipRow}>
                                          <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 15–40 min</span>
                                          <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Service</span>
                                        </div>
                                      </div>
                                      <div style={S.ssOptionRight}>
                                        <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                        <div style={S.ssOptionPriceUnit}>{startingPrice != null ? "starting" : "consultation"}</div>
                                        <div style={S.ssOptionQtyRow}>
                                          <button
                                            type="button"
                                            style={qtyForGroup === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                            onClick={() => qtyForGroup > 0 && openModal(group)}
                                            disabled={qtyForGroup === 0}
                                            aria-label={`Edit ${group.label} quantity`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <span style={S.qtyValueText}>{qtyForGroup}</span>
                                          <button
                                            type="button"
                                            style={S.elecQtyBtnP}
                                            onClick={() => openModal(group)}
                                            aria-label={`Add ${group.label}`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          );
                        }

                        // Inverter UPS: the real component list (Inverter,
                        // UPS, Battery, …) instead of the generic single
                        // detail card — each opens the work-type popup below.
                        if (isInverterUps) {
                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionList} className="cn-ss-option-list">
                                {(service.components || []).map((component) => {
                                  const qtyForComponent = selectedElecServices
                                    .filter((s) => (component.exactWork || []).some((w) => s.value.startsWith(w.value)))
                                    .reduce((sum, s) => sum + s.quantity, 0);
                                  const prices = (component.exactWork || []).map((w) => w.price).filter((p) => typeof p === "number");
                                  const startingPrice = prices.length ? Math.min(...prices) : null;

                                  return (
                                    <div key={component.key} style={S.ssOptionRow} className="cn-ss-option-row">
                                      <img
                                        src={component.image || service.image}
                                        alt=""
                                        style={S.ssOptionImg}
                                        onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                      />
                                      <div style={S.ssOptionText}>
                                        <div style={S.ssOptionLabel}>{component.label}</div>
                                        <div style={S.ssOptionDesc}>
                                          {(component.exactWork || []).length > 0
                                            ? "Repair, replacement, new installation or removal."
                                            : "Not sure what's wrong? Book a consultation."}
                                        </div>
                                        <div style={S.ssOptionChipRow}>
                                          <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 20–50 min</span>
                                          <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Service</span>
                                        </div>
                                      </div>
                                      <div style={S.ssOptionRight}>
                                        <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                        <div style={S.ssOptionPriceUnit}>{startingPrice != null ? "starting" : "consultation"}</div>
                                        <div style={S.ssOptionQtyRow}>
                                          <button
                                            type="button"
                                            style={qtyForComponent === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                            onClick={() => qtyForComponent > 0 && openIvModal(component)}
                                            disabled={qtyForComponent === 0}
                                            aria-label={`Edit ${component.label} quantity`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <span style={S.qtyValueText}>{qtyForComponent}</span>
                                          <button
                                            type="button"
                                            style={S.elecQtyBtnP}
                                            onClick={() => openIvModal(component)}
                                            aria-label={`Add ${component.label}`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          );
                        }

                        // Fan: the real fan-category list (Ceiling, Wall,
                        // Exhaust, BLDC/Smart, Heavy-Duty) instead of the
                        // generic single detail card — each opens the
                        // Work Type → Type popup below. Categories are
                        // shared across all 3 real work types, so "repair"
                        // is used as the canonical list/image/slug source.
                        if (isFan) {
                          const repairCategories = (service.workTypes || []).find((w) => w.key === "repair")?.categories || [];
                          return (
                            <>
                              <div style={S.elecMainHeader}>
                                <img
                                  src={service.image}
                                  alt={service.label}
                                  style={S.elecMainImg}
                                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                />
                                <div>
                                  <div style={S.elecMainTitle}>{service.label} Services</div>
                                  <div style={S.elecMainSub}>{description}</div>
                                </div>
                              </div>

                              <div style={S.ssOptionList} className="cn-ss-option-list">
                                {repairCategories.map((cat) => {
                                  const slug = cat.types?.[0]?.value.split("_")[2] || "";
                                  const qtyForCategory = selectedElecServices
                                    .filter((s) => s.value.startsWith("fan_") && s.value.split("_")[2] === slug)
                                    .reduce((sum, s) => sum + s.quantity, 0);
                                  const prices = (cat.types || []).map((t) => t.price).filter((p) => typeof p === "number");
                                  const startingPrice = prices.length ? Math.min(...prices) : null;

                                  return (
                                    <div key={cat.label} style={S.ssOptionRow} className="cn-ss-option-row">
                                      <img
                                        src={cat.types?.[0]?.image || service.image}
                                        alt=""
                                        style={S.ssOptionImg}
                                        onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                                      />
                                      <div style={S.ssOptionText}>
                                        <div style={S.ssOptionLabel}>{cat.label}</div>
                                        <div style={S.ssOptionDesc}>Repair, install or remove.</div>
                                        <div style={S.ssOptionChipRow}>
                                          <span style={S.ssOptionChip}><Clock3 size={12} /> Estimated time 20–40 min</span>
                                          <span style={S.ssOptionChip}><ShieldCheck size={12} /> Professional Service</span>
                                        </div>
                                      </div>
                                      <div style={S.ssOptionRight}>
                                        <div style={S.ssOptionPriceValue}>{startingPrice != null ? `₹${startingPrice}` : "—"}</div>
                                        <div style={S.ssOptionPriceUnit}>starting</div>
                                        <div style={S.ssOptionQtyRow}>
                                          <button
                                            type="button"
                                            style={qtyForCategory === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                            onClick={() => qtyForCategory > 0 && openFnModal(cat.label)}
                                            disabled={qtyForCategory === 0}
                                            aria-label={`Edit ${cat.label} quantity`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <span style={S.qtyValueText}>{qtyForCategory}</span>
                                          <button
                                            type="button"
                                            style={S.elecQtyBtnP}
                                            onClick={() => openFnModal(cat.label)}
                                            aria-label={`Add ${cat.label}`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          );
                        }

                        return (
                          <>
                            <div style={S.elecMainHeader}>
                              <img
                                src={service.image}
                                alt={service.label}
                                style={S.elecMainImg}
                                onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                              />
                              <div>
                                <div style={S.elecMainTitle}>{service.label} Services</div>
                                <div style={S.elecMainSub}>{description}</div>
                              </div>
                            </div>

                            <div
                              className="cn-elec-detail-card"
                              style={{ ...S.elecDetailCard, cursor: isPlaceholder || reachedMax ? "default" : "pointer" }}
                              onClick={handlePrimaryAction}
                              role={isPlaceholder ? undefined : "button"}
                              tabIndex={isPlaceholder ? undefined : 0}
                              onKeyDown={(e) => {
                                if (!isPlaceholder && (e.key === "Enter" || e.key === " ")) {
                                  e.preventDefault();
                                  handlePrimaryAction();
                                }
                              }}
                            >
                              <img
                                src={service.image}
                                alt={service.label}
                                style={S.elecDetailImg}
                                onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                              />
                              <div style={S.elecDetailBody}>
                                <div style={S.elecDetailTitle}>{service.label}</div>
                                <div style={S.elecDetailDesc}>{description}</div>
                                {!isPlaceholder && !isWorkTypeGroup && !isGroup && (
                                  <div style={S.elecPriceText}>Starts at ₹{cityPrice(service.value, service.price)}</div>
                                )}
                              </div>
                              <div className="cn-elec-detail-action" style={S.elecDetailAction} onClick={(e) => e.stopPropagation()}>
                                {isPlaceholder ? (
                                  <div style={S.elecComingSoon}>Coming Soon</div>
                                ) : (isWorkTypeGroup || isGroup) ? (
                                  <button
                                    type="button"
                                    style={reachedMax ? S.elecSelectBtnDisabled : (qty > 0 ? S.elecSelectBtnActive : S.elecSelectBtn)}
                                    onClick={handlePrimaryAction}
                                    disabled={reachedMax}
                                  >
                                    {qty > 0 ? <CheckCircle2 size={14} /> : <ArrowRight size={14} />}
                                    {qty > 0
                                      ? `${qty} ${service.unit || "unit"}${qty > 1 ? "s" : ""} Added`
                                      : service.detailRoute ? "View Details" : "Select Option"}
                                  </button>
                                ) : (
                                  <div style={S.elecQtyRow}>
                                    <button
                                      type="button"
                                      style={qty === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                                      onClick={() => qty > 0 && updateElecQty(service.value, -1, service.limit)}
                                      disabled={qty === 0}
                                    >
                                      <Minus size={13} />
                                    </button>
                                    <span style={S.elecQtyText}>{qty || "Not added"}</span>
                                    <button
                                      type="button"
                                      style={reachedMax ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                                      onClick={() => addElecService(service)}
                                      disabled={reachedMax}
                                    >
                                      <Plus size={13} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* ── Right: live "Your Booking" cart panel ──────────────── */}
                    <div style={S.elecCartPanel} className="cn-elec-cart-panel">
                      <div style={S.elecCartHeader}>
                        <span style={S.elecCartTitle}>
                          Your Booking{(elecTotalQty + pendingTechnicalCartItems.length) > 0 ? ` (${elecTotalQty + pendingTechnicalCartItems.length} item${(elecTotalQty + pendingTechnicalCartItems.length) === 1 ? "" : "s"})` : ""}
                        </span>
                        {selectedElecServices.length > 0 && (
                          <button type="button" style={S.elecCartClearBtn} onClick={() => setSelectedElecServices([])}>
                            Clear All
                          </button>
                        )}
                      </div>

                      {/* Already-scheduled items from a dedicated wizard
                          (Switch & Socket, CCTV, …) — each has its own
                          bookingDate/bookingTime already, so it's shown
                          read-only with just a Remove option, not folded
                          into the quantity-based list below. */}
                      {pendingTechnicalCartItems.length > 0 && (
                        <div style={S.elecCartList}>
                          {pendingTechnicalCartItems.map((item) => (
                            <div key={item.id} style={S.elecCartRow}>
                              <img
                                src={item.image || DEFAULT_HERO_IMAGE}
                                alt={item.title}
                                style={S.elecCartImg}
                                onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                              />
                              <div style={S.elecCartRowBody}>
                                <div style={S.elecCartRowName}>{item.title}</div>
                                <div style={S.elecCartRowPrice}>₹{Number(item.price || 0).toFixed(2)}</div>
                                <div style={S.elecQtyText}>
                                  {item.bookingDate && item.bookingTime ? `${item.bookingDate} · ${item.bookingTime}` : "Already scheduled"}
                                </div>
                              </div>
                              <button
                                type="button"
                                style={S.elecCartRemoveBtn}
                                onClick={() => removePendingTechnicalCartItem(item.id)}
                                aria-label={`Remove ${item.title}`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedElecServices.length === 0 && pendingTechnicalCartItems.length === 0 ? (
                        <div style={S.elecCartEmpty}>No services added yet — pick one from the left to get started.</div>
                      ) : selectedElecServices.length === 0 ? null : (
                        <div style={S.elecCartList}>
                          {selectedElecServices.map((item) => (
                            <div key={item.value} style={S.elecCartRow}>
                              <img
                                src={item.image || DEFAULT_HERO_IMAGE}
                                alt={item.label}
                                style={S.elecCartImg}
                                onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                              />
                              <div style={S.elecCartRowBody}>
                                <div style={S.elecCartRowName}>
                                  {item.parentLabel ? `${item.parentLabel} — ${item.label}` : item.label}
                                  {item.is_consultation && <span style={S.elecConsultationBadge}>Consultation</span>}
                                </div>
                                <div style={S.elecCartRowPrice}>₹{resolvedElecPrice(item)}</div>
                                <div style={S.elecQtyRow}>
                                  <button type="button" style={S.elecQtyBtn} onClick={() => updateElecQty(item.value, -1, item.limit)}>
                                    <Minus size={12} />
                                  </button>
                                  <span style={S.elecQtyText}>{item.quantity}</span>
                                  <button
                                    type="button"
                                    style={item.quantity >= (item.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                                    onClick={() => updateElecQty(item.value, 1, item.limit)}
                                    disabled={item.quantity >= (item.limit || 10)}
                                  >
                                    <Plus size={12} />
                                  </button>
                                </div>
                              </div>
                              <button
                                type="button"
                                style={S.elecCartRemoveBtn}
                                onClick={() => updateElecQty(item.value, -item.quantity, item.limit)}
                                aria-label={`Remove ${item.label}`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={S.elecCartTotalRow}>
                        <span>Estimated Amount</span>
                        <span>₹{(elecSubtotal + pendingTechnicalTotal).toFixed(2)}</span>
                      </div>

                      {selectedElecServices.length > 0 ? (
                        <button
                          type="button"
                          style={S.continueBtn}
                          onClick={confirmElecServices}
                        >
                          Continue to Date &amp; Time <ArrowRight size={16} />
                        </button>
                      ) : pendingTechnicalCartItems.length > 0 ? (
                        // Nothing left on this page to schedule — the only
                        // items are already-scheduled ones from a dedicated
                        // wizard, so skip straight to checkout with those.
                        <button
                          type="button"
                          style={S.continueBtn}
                          onClick={handleCheckoutPendingTechnicalOnly}
                        >
                          Proceed to Checkout <ArrowRight size={16} />
                        </button>
                      ) : (
                        <button type="button" style={{ ...S.continueBtn, ...S.continueBtnDisabled }} disabled>
                          Continue to Date &amp; Time <ArrowRight size={16} />
                        </button>
                      )}

                      <div style={S.elecPromiseBox}>
                        <div style={S.elecPromiseTitle}><ShieldCheck size={16} color="#16a34a" /> ClickNido Promise</div>
                        <ul style={S.elecPromiseList}>
                          <li>Verified &amp; Trained Professionals</li>
                          <li>OTP Verified Service</li>
                          <li>Transparent Pricing</li>
                          <li>Book up to {DAYS_AHEAD} days in advance</li>
                        </ul>
                      </div>
                    </div>

                    {/* Mobile-only sticky bottom bar — hidden by default
                        (display:none inline), shown via the cn-elec-mobile-bar
                        media-query override above at ≤767px. Mirrors the same
                        total/continue logic as the full "Your Booking" panel
                        above it, just condensed for small screens. */}
                    <div style={S.elecMobileBar} className="cn-elec-mobile-bar">
                      <div style={S.elecMobileBarInfo}>
                        <div style={S.elecMobileBarCount}>
                          {elecTotalQty + pendingTechnicalCartItems.length} item{(elecTotalQty + pendingTechnicalCartItems.length) === 1 ? "" : "s"}
                        </div>
                        <div style={S.elecMobileBarAmount}>₹{(elecSubtotal + pendingTechnicalTotal).toFixed(2)}</div>
                      </div>
                      {selectedElecServices.length > 0 ? (
                        <button type="button" style={S.elecMobileBarBtn} onClick={confirmElecServices}>
                          Continue <ArrowRight size={15} />
                        </button>
                      ) : pendingTechnicalCartItems.length > 0 ? (
                        <button type="button" style={S.elecMobileBarBtn} onClick={handleCheckoutPendingTechnicalOnly}>
                          Checkout <ArrowRight size={15} />
                        </button>
                      ) : (
                        <button type="button" style={{ ...S.elecMobileBarBtn, ...S.continueBtnDisabled }} disabled>
                          Continue <ArrowRight size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : category.key === "home_cleaning" ? (
                  <>
                    {/* Informational showcase, not a picker — every visit
                        covers all of these at the flat duration price
                        (see HOME_CLEANING_SCOPE above), so there's nothing
                        to tick. Scrolling to the sentinel below unlocks
                        Continue. */}
                    <div style={S.serviceGrid}>
                      {HOME_CLEANING_SHOWCASE.map((svc) => (
                        <div key={svc.name} style={{ ...S.serviceCard, cursor: "default" }}>
                          {/* QAS_* artwork already has the service name baked
                              in — no separate text title needed here. */}
                          <img src={svc.image} alt={svc.name} style={S.serviceImg} />
                          <div style={S.scheduleBadge}><CalendarDays size={11} /> Schedule</div>
                        </div>
                      ))}
                    </div>
                    <div ref={homeCleaningEndRef} style={S.scrollSentinel}>
                      {homeCleaningReady ? (
                        <span style={S.scrollSentinelDone}><CheckCircle2 size={14} color="#16a34a" /> You've seen the full list — ready to continue</span>
                      ) : (
                        <span>Scroll to see all 21 services…</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={S.serviceGrid}>
                    {categoryServices.map((svc) => {
                      const checked = selectedIds.includes(svc.id);
                      return (
                        <button
                          key={svc.id} type="button"
                          style={{ ...S.serviceCard, ...(checked ? S.serviceCardActive : {}) }}
                          onClick={() => toggleService(svc)}
                        >
                          <img
                            src={getServiceImage(svc.name)}
                            alt={svc.name}
                            style={S.serviceImg}
                          />
                          <div style={S.scheduleBadge}><CalendarDays size={11} /> Schedule</div>
                          <div style={S.serviceBody}>
                            <div style={S.serviceCheckRow}>
                              {checked ? <CheckCircle2 size={18} color="#16a34a" /> : <Circle size={18} color="#d1d5db" />}
                              <span style={S.serviceName}>{svc.name}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Electrical: this footer's job (the "Continue" CTA + the
                    days-in-advance note) now lives in the right-side "Your
                    Booking" cart panel above instead — see elecPromiseBox. */}
                {!isElectricalCategory && (
                  <div style={S.servicesFooterBar}>
                    <div style={S.servicesFooterLeft}>
                      <div style={S.servicesFooterIconWrap}><CalendarCheck2 size={18} color="#16a34a" /></div>
                      <div>
                        <div style={S.servicesFooterTitle}>Book up to {DAYS_AHEAD} days in advance</div>
                        <div style={S.servicesFooterText}>
                          {category.key === "home_cleaning"
                            ? "Pick your preferred date, duration and time next."
                            : "Pick your preferred date and time after selecting services."}
                        </div>
                      </div>
                    </div>
                    {category.key === "home_cleaning" ? (
                      <button
                        type="button"
                        style={{ ...S.continueBtn, ...S.servicesFooterBtn, ...(!homeCleaningReady ? S.continueBtnDisabled : {}) }}
                        disabled={!homeCleaningReady}
                        onClick={confirmServices}
                      >
                        Continue <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        style={{ ...S.continueBtn, ...S.servicesFooterBtn, ...(selectedIds.length === 0 ? S.continueBtnDisabled : {}) }}
                        disabled={selectedIds.length === 0}
                        onClick={confirmServices}
                      >
                        {isMultiSelect ? `Continue (${selectedIds.length} selected)` : "Continue"} <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── Step 3: Duration + Date + Time, all on one screen ─────── */}
            {step === "schedule" && category && (
              <>
                <div style={S.summaryBar}>
                  <img
                    src={isElectricalCategory ? (selectedElecServices[0]?.image || DEFAULT_HERO_IMAGE) : getServiceImage(selectedServices[0]?.name)}
                    alt={category.label}
                    style={S.summaryImg}
                    onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                  />
                  <div style={S.summaryBody}>
                    <div style={S.summaryTitle}>{category.label}</div>
                    <div style={S.summaryChipRow}>
                      {isElectricalCategory ? (
                        <>
                          {selectedElecServices.map((s) => (
                            <span key={s.value} style={S.summaryChip}>
                              {s.parentLabel ? `${s.parentLabel} - ${s.label}` : s.label} × {s.quantity}
                            </span>
                          ))}
                          <span style={S.summaryDurationChip}>{fmt(elecGrandTotal)}</span>
                        </>
                      ) : (
                        <>
                          {selectedServices.map((s) => (
                            <span key={s.id} style={S.summaryChip}>{s.name}</span>
                          ))}
                          <span style={S.summaryDurationChip}>{duration || 1} Hour Service</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button type="button" style={S.summaryChangeBtn} onClick={changeSelection}>
                    <Pencil size={13} /> Change
                  </button>
                </div>

                <div style={S.scheduleHead}>
                  <div>
                    <h2 style={S.sectionTitle}>Choose Date{isElectricalCategory ? "" : ", Duration"} &amp; Time</h2>
                    <p style={S.scheduleHeadSub}>Book up to {DAYS_AHEAD} days in advance</p>
                  </div>
                  <div style={S.legend}>
                    <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#22c55e" }} /> Available</span>
                    <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#ef4444" }} /> Booked</span>
                    <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#9ca3af" }} /> Unavailable</span>
                  </div>
                </div>

                <div style={{ ...S.scheduleColumns, ...(isElectricalCategory ? { gridTemplateColumns: "repeat(2, 1fr)" } : {}) }}>
                  {/* Column 1 — Date */}
                  <div style={S.scheduleCol}>
                    <div style={S.scheduleColTitle}>1. Select Date</div>
                    {loadingAvailability ? (
                      <div style={S.stateBox}>Loading…</div>
                    ) : availabilityError ? (
                      <div style={S.errorBox}>{availabilityError}</div>
                    ) : (
                      <div style={S.dateList}>
                        {days.map((day) => {
                          const hasOpen  = day.slots.some((s) => isSlotSelectable(s, day.date));
                          const selected = selectedDate === day.date;
                          return (
                            <button
                              key={day.date} type="button"
                              disabled={!hasOpen}
                              style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                              onClick={() => hasOpen && pickDate(day.date)}
                            >
                              <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                              <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                              <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                                {hasOpen ? `${day.slots.filter((s) => isSlotSelectable(s, day.date)).length} open` : "Full"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div style={S.scheduleNote}>
                      <CalendarCheck2 size={14} color="#16a34a" /> You can book today or up to {DAYS_AHEAD} days in advance.
                    </div>
                  </div>

                  {/* Column 2 — Duration (not applicable to itemized/technical pricing) */}
                  {!isElectricalCategory && (
                    <div style={S.scheduleCol}>
                      <div style={S.scheduleColTitle}>2. Select Duration</div>
                      <div style={S.durationList}>
                        {DURATIONS.map((d) => {
                          const Icon = d.icon;
                          const selected = duration === d.value;
                          return (
                            <button
                              key={d.value} type="button"
                              style={{ ...S.durationRow, ...(selected ? S.durationRowActive : {}) }}
                              onClick={() => pickDuration(d.value)}
                            >
                              <div style={S.durationRowIconWrap}><Icon size={18} color="#16a34a" /></div>
                              <div style={S.durationRowBody}>
                                <div style={S.durationRowTitle}>{d.title} Service</div>
                                <div style={S.durationRowTagline}>{d.tagline}</div>
                              </div>
                              {selected ? <CheckCircle2 size={18} color="#16a34a" /> : <Circle size={18} color="#d1d5db" />}
                            </button>
                          );
                        })}
                      </div>
                      <div style={S.scheduleNote}>
                        <Info size={14} color="#16a34a" /> Price is flat per visit duration, not per service.
                      </div>
                    </div>
                  )}

                  {/* Column 3 — Time slot */}
                  <div style={S.scheduleCol}>
                    <div style={S.scheduleColTitle}>{isElectricalCategory ? "2" : "3"}. Select Time Slot</div>
                    <div style={S.slotGrid}>
                      {(activeDay?.slots || []).map((slot) => {
                        const state      = getSlotState(slot, activeDay?.date);
                        const selectable = state === "available";
                        const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                        const selected   = selectedTime === slot.time;
                        return (
                          <button
                            key={`${activeDay?.date}-${slot.time}`} type="button"
                            disabled={!selectable}
                            style={{
                              ...S.slotBtn,
                              ...(selected ? S.slotBtnActive : {}),
                              ...(state === "booked" ? S.slotBtnBooked : {}),
                              ...(state === "unavailable" || state === "too-soon" ? S.slotBtnDisabled : {}),
                            }}
                            onClick={() => selectable && pickTime(slot.time, slot._id)}
                          >
                            <div style={S.slotTimeRow}>
                              {/* Technical's slots are stored/keyed as full
                                  "start - end" range strings (e.g. "08:00 AM
                                  - 09:00 AM") — only the start time is shown
                                  here per request; pickTime() below still
                                  gets the full original string unchanged. */}
                              <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time.split(" - ")[0]}</span>
                              {selected && <CheckCircle2 size={13} color="#fff" />}
                            </div>
                            <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                              {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : state === "too-soon" ? "Too soon" : "Unavailable"}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  style={{ ...S.continueBtn, ...(!selectedTime ? S.continueBtnDisabled : {}) }}
                  disabled={!selectedTime}
                  onClick={isElectricalCategory ? handleContinueElectrical : handleContinue}
                >
                  {isUserLoggedIn() ? "Continue to Review" : "Login & Continue"} <ArrowRight size={16} />
                </button>
              </>
            )}
          </>
        )}
      </div>

      {socketModalOpen && activeSocketService && (
        <div style={S.modalOverlay} onClick={closeSocketModal}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <h3 style={S.modalTitle}>{activeSocketService.label}</h3>
                <div style={S.modalSub}>Select an option and add to booking summary</div>
              </div>
              <button type="button" style={S.modalCloseBtn} onClick={closeSocketModal}>
                <X size={18} />
              </button>
            </div>

            <div style={S.modalBody}>
              {activeSocketService.subServices?.map((sub) => {
                const selectedItem = selectedElecServices.find((s) => s.value === sub.value);
                const qty = selectedItem?.quantity || 0;
                const parentQty = getElecParentSelectedQty(activeSocketService.value);
                const reachedMax = parentQty >= (activeSocketService.limit || 5);

                return (
                  <div key={sub.value} style={S.socketOptionCard}>
                    <div>
                      <div style={S.socketOptionTitle}>{sub.label}</div>
                      <div style={S.socketOptionPrice}>₹{cityPrice(sub.value, sub.price)}</div>
                    </div>
                    <div style={S.socketOptionActions}>
                      <button
                        type="button"
                        style={qty === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => qty > 0 && updateElecQty(sub.value, -1, sub.limit)}
                        disabled={qty === 0}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{qty}</div>
                      <button
                        type="button"
                        style={reachedMax ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => addSocketSubService(activeSocketService, sub)}
                        disabled={reachedMax}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {workTypeModalOpen && activeWorkTypeService && (
        <div style={S.modalOverlay} onClick={closeWorkTypeModal}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <h3 style={S.modalTitle}>{activeWorkTypeService.label}</h3>
                <div style={S.modalSub}>Select a work type, then the fan type</div>
              </div>
              <button type="button" style={S.modalCloseBtn} onClick={closeWorkTypeModal}>
                <X size={18} />
              </button>
            </div>

            <div style={S.workTypeTabRow}>
              {activeWorkTypeService.workTypes.map((wt) => (
                <button
                  key={wt.key}
                  type="button"
                  style={activeWorkTypeKey === wt.key ? S.workTypeTabActive : S.workTypeTab}
                  onClick={() => setActiveWorkTypeKey(wt.key)}
                >
                  {wt.label}
                </button>
              ))}
            </div>

            <div style={S.modalBody}>
              {(() => {
                const activeWorkType = activeWorkTypeService.workTypes.find((wt) => wt.key === activeWorkTypeKey);
                if (!activeWorkType) return null;

                if (activeWorkType.flat) {
                  const leaf = activeWorkType.flat;
                  const composedValue = leaf.value;
                  const selectedItem = selectedElecServices.find((s) => s.value === composedValue);
                  const qty = selectedItem?.quantity || 0;
                  const parentQty = getElecParentSelectedQty(activeWorkTypeService.value);
                  const reachedMax = parentQty >= (activeWorkTypeService.limit || 10);

                  return (
                    <div style={S.socketOptionCard}>
                      <div>
                        <div style={S.socketOptionTitle}>{activeWorkType.label} — {leaf.label}</div>
                        <div style={S.socketOptionPrice}>₹{cityPrice(leaf.value, leaf.price)}</div>
                      </div>
                      <div style={S.socketOptionActions}>
                        <button
                          type="button"
                          style={qty === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                          onClick={() => qty > 0 && updateElecQty(composedValue, -1, leaf.limit)}
                          disabled={qty === 0}
                        >
                          <Minus size={13} />
                        </button>
                        <div style={S.qtyValueText}>{qty}</div>
                        <button
                          type="button"
                          style={reachedMax ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                          onClick={() => addWorkTypeLeaf(activeWorkTypeService, activeWorkType.label, null, leaf)}
                          disabled={reachedMax}
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    </div>
                  );
                }

                return activeWorkType.categories.map((cat) => (
                  <div key={cat.label} style={S.workTypeCategoryBlock}>
                    <div style={S.workTypeCategoryTitle}>{cat.label}</div>
                    {cat.types.map((leaf) => {
                      const selectedItem = selectedElecServices.find((s) => s.value === leaf.value);
                      const qty = selectedItem?.quantity || 0;
                      const parentQty = getElecParentSelectedQty(activeWorkTypeService.value);
                      const reachedMax = parentQty >= (activeWorkTypeService.limit || 10);

                      return (
                        <div key={leaf.value} style={S.socketOptionCard}>
                          <div>
                            <div style={S.socketOptionTitle}>{leaf.label}</div>
                            <div style={S.socketOptionPrice}>₹{cityPrice(leaf.value, leaf.price)}</div>
                          </div>
                          <div style={S.socketOptionActions}>
                            <button
                              type="button"
                              style={qty === 0 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                              onClick={() => qty > 0 && updateElecQty(leaf.value, -1, leaf.limit)}
                              disabled={qty === 0}
                            >
                              <Minus size={13} />
                            </button>
                            <div style={S.qtyValueText}>{qty}</div>
                            <button
                              type="button"
                              style={reachedMax ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                              onClick={() => addWorkTypeLeaf(activeWorkTypeService, activeWorkType.label, cat.label, leaf)}
                              disabled={reachedMax}
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {ssModalOpen && ssOption && (() => {
        const service = electricalCatalog.find((s) => s.value === "switch-socket");
        if (!service) return null;
        const ratings = service.ratings || [];
        const isPlug = ssOption.itemLabel === "Plug";
        const isEvPlug = isPlug && ssPlugCategory === "ev_plug";
        const plugLeadInReady = !isPlug || (Boolean(ssVehicleType) && Boolean(ssPlugCategory) && (isEvPlug || Boolean(ssPlugType)));
        const canAdd = ssOption.isConsultation || (plugLeadInReady && Boolean(ssRating));
        const ratingInfo = ssRatingsReviews[ssOption.key];

        return (
          <div style={S.modalOverlay} onClick={closeSsModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{ssOption.label}</h3>
                  {ratingInfo && (
                    <div style={S.ssOptionRating}>
                      ⭐ {ratingInfo.average_rating.toFixed(2)} ({formatReviewCount(ratingInfo.review_count)} reviews)
                    </div>
                  )}
                  <div style={S.modalSub}>
                    {ssOption.isConsultation
                      ? `Flat consultation charge — ₹${service.consultationFee || 90}`
                      : "Pick a type, quantity and who supplies the material"}
                  </div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeSsModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                {ssOption.isConsultation ? (
                  <div style={S.ssConsultationNote}>
                    An electrician will inspect the issue on-site and advise before any work starts —
                    no separate rating, quantity or material needed for a consultation visit.
                  </div>
                ) : (
                  <>
                    {isPlug && (
                      <>
                        <div style={S.ssFieldLabel}>Vehicle Type</div>
                        <div style={S.ssRatingRow}>
                          {VEHICLE_TYPES.map((v) => (
                            <button
                              key={v.value}
                              type="button"
                              style={ssVehicleType === v.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                              onClick={() => { setSsVehicleType(v.value); setSsPlugCategory(null); setSsPlugType(null); setSsRating(null); }}
                            >
                              {ssVehicleType === v.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                              {v.label}
                            </button>
                          ))}
                        </div>

                        {ssVehicleType && (
                          <>
                            <div style={S.ssFieldLabel}>Plug or EV Plug?</div>
                            <div style={S.ssMaterialRow}>
                              {PLUG_CATEGORIES.map((c) => (
                                <button
                                  key={c.value}
                                  type="button"
                                  style={ssPlugCategory === c.value ? S.ssMaterialBtnActive : S.ssMaterialBtn}
                                  onClick={() => { setSsPlugCategory(c.value); setSsPlugType(null); setSsRating(null); }}
                                >
                                  {ssPlugCategory === c.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}

                        {ssPlugCategory === "plug" && (
                          <>
                            <div style={S.ssFieldLabel}>Plug Type</div>
                            <div style={S.ssRatingRow}>
                              {PLUG_TYPES.map((pt) => (
                                <button
                                  key={pt.value}
                                  type="button"
                                  style={ssPlugType === pt.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                                  onClick={() => setSsPlugType(pt.value)}
                                >
                                  {ssPlugType === pt.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                                  {pt.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {plugLeadInReady && (
                      <>
                        <div style={S.ssFieldLabel}>Type</div>
                        <div style={S.ssRatingRow}>
                          {ratings.map((rating) => (
                            <button
                              key={rating.value}
                              type="button"
                              style={ssRating === rating.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                              onClick={() => setSsRating(rating.value)}
                            >
                              {ssRating === rating.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                              {rating.label}
                              {typeof rating.basePrice === "number" && (
                                <span style={S.ssRatingPrice}>₹{rating.basePrice}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {plugLeadInReady && ssRating && (
                      <>
                        <div style={S.ssFieldLabel}>Quantity</div>
                        <div style={S.ssQtyRow}>
                          <button
                            type="button"
                            style={ssQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                            onClick={() => setSsQty((q) => Math.max(1, q - 1))}
                            disabled={ssQty <= 1}
                          >
                            <Minus size={13} />
                          </button>
                          <div style={S.qtyValueText}>{ssQty}</div>
                          <button
                            type="button"
                            style={ssQty >= (service.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                            onClick={() => setSsQty((q) => Math.min(service.limit || 10, q + 1))}
                            disabled={ssQty >= (service.limit || 10)}
                          >
                            <Plus size={13} />
                          </button>
                        </div>

                        <div style={S.ssFieldLabel}>Material</div>
                        <div style={S.ssMaterialRow}>
                          <button
                            type="button"
                            style={ssMaterial === "clicknido" ? S.ssMaterialBtnActive : S.ssMaterialBtn}
                            onClick={() => setSsMaterial("clicknido")}
                          >
                            {ssMaterial === "clicknido" ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                            Company Material
                          </button>
                          <button
                            type="button"
                            style={ssMaterial === "customer" ? S.ssMaterialBtnActive : S.ssMaterialBtn}
                            onClick={() => setSsMaterial("customer")}
                          >
                            {ssMaterial === "customer" ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                            Customer Material
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmSsAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* "View details & ratings" — photo, customer notes/instructions
          (reuses the switch-socket service's own real includes/excludes
          from pricing.js) and a handful of sample customer reviews from
          ratings_reviews_col. View-only; "Add to Cart" here hands off to
          the real Type/Quantity/Material popup above. */}
      {ssDetailsOpen && ssDetailsOption && (() => {
        const service = electricalCatalog.find((s) => s.value === "switch-socket");
        if (!service) return null;
        const ratingInfo = ssRatingsReviews[ssDetailsOption.key];
        const reviews = ratingInfo?.reviews || [];

        return (
          <div style={S.modalOverlay} onClick={closeSsDetailsModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{ssDetailsOption.label}</h3>
                  {ratingInfo && (
                    <div style={S.ssOptionRating}>
                      ⭐ {ratingInfo.average_rating.toFixed(2)} ({formatReviewCount(ratingInfo.review_count)} reviews)
                    </div>
                  )}
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeSsDetailsModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <img
                  src={ssDetailsOption.image || service.image}
                  alt={ssDetailsOption.label}
                  style={S.ssDetailsPhoto}
                  onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                />

                {(() => {
                  // Switch/Socket types supported — Plug/Consultation have no
                  // structured type catalog, so this section is skipped for
                  // them (reuses service.subCategories, the same real
                  // switch/socket type lists the generic fallback-card view
                  // uses — never a separate hand-copied list).
                  const subKey = ssDetailsOption.itemLabel === "Switch" ? "switch"
                    : ssDetailsOption.itemLabel === "Socket" ? "socket"
                    : null;
                  if (!subKey) return null;
                  const types = service.subCategories?.find((c) => c.key === subKey)?.types || [];
                  if (types.length === 0) return null;
                  return (
                    <>
                      <div style={S.ssFieldLabel}>{ssDetailsOption.itemLabel} Types Supported</div>
                      <div style={S.ssOptionChipRow}>
                        {types.map((t) => (
                          <span key={t.value} style={S.ssOptionChip}>{t.label}</span>
                        ))}
                      </div>
                    </>
                  );
                })()}

                <div style={S.ssFieldLabel}>Customer Notes &amp; Instructions</div>
                {Array.isArray(service.includes) && service.includes.length > 0 && (
                  <ul style={S.ssNotesList}>
                    {service.includes.map((line, i) => (
                      <li key={`inc-${i}`} style={S.ssNotesItemIncluded}><CheckCircle2 size={13} color="#16a34a" /> {line}</li>
                    ))}
                    {(service.excludes || []).map((line, i) => (
                      <li key={`exc-${i}`} style={S.ssNotesItemExcluded}><XCircle size={13} color="#dc2626" /> {line}</li>
                    ))}
                  </ul>
                )}

                <div style={S.ssFieldLabel}>Customer Feedback</div>
                {reviews.length > 0 ? (
                  <div style={S.ssReviewList}>
                    {reviews.map((r, i) => (
                      <div key={i} style={S.ssReviewCard}>
                        <div style={S.ssReviewTop}>
                          <span style={S.ssReviewName}>{r.name}</span>
                          <span style={S.ssReviewStars}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                        </div>
                        <div style={S.ssReviewComment}>{r.comment}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={S.ssConsultationNote}>No sample reviews yet for this service.</div>
                )}

                <button
                  type="button"
                  style={S.ssAddBtn}
                  onClick={() => switchFromDetailsToAdd(ssDetailsOption)}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Appliance — work type (Install/Repair/Replace/Remove/Consultation)
          → specific sub-service (only when a work type has more than one,
          e.g. Install's "New Installation" vs "Wiring & Switch Box") →
          Install Requirements (only for an isInstall sub-service — the
          existing extra step already defined on the service) → quantity →
          Add to Cart. */}
      {apModalOpen && apAppliance && (() => {
        const service = electricalCatalog.find((s) => s.value === "appliance");
        if (!service) return null;
        const workTypes = apAvailableWorkTypes(apAppliance);
        const subChoices = apWorkType ? apSubServicesFor(apAppliance, apWorkType) : [];
        const needsSubChoice = subChoices.length > 1;
        const needsInstallReq = Boolean(apSubService?.isInstall);
        const canAdd = Boolean(apSubService) && (!needsInstallReq || Boolean(apInstallReq));

        // "Not Sure" has no structured sub-services at all — straight to a
        // single Consultation line, no work-type picker needed.
        if (apAppliance.subServices.length === 0) {
          return (
            <div style={S.modalOverlay} onClick={closeApModal}>
              <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.modalHeader}>
                  <div>
                    <h3 style={S.modalTitle}>{apAppliance.label}</h3>
                    <div style={S.modalSub}>Flat consultation charge — ₹{service.consultation?.price || 99}</div>
                  </div>
                  <button type="button" style={S.modalCloseBtn} onClick={closeApModal}>
                    <X size={18} />
                  </button>
                </div>
                <div style={S.modalBody}>
                  <div style={S.ssConsultationNote}>
                    An electrician will inspect the appliance on-site and advise before any work starts.
                  </div>
                  <button
                    type="button"
                    style={S.ssAddBtn}
                    onClick={() => {
                      const value = service.consultation?.value || "appliance_consultation";
                      const price = service.consultation?.price || 99;
                      setSelectedElecServices((prev) => {
                        const exists = prev.find((s) => s.value === value);
                        if (exists) return prev.map((s) => (s.value === value ? { ...s, quantity: Math.min(s.quantity + 1, 5) } : s));
                        return [...prev, { value, label: `${apAppliance.label} — Consultation`, price, quantity: 1, image: apAppliance.image || service.image, limit: 5, noCityPrice: true }];
                      });
                      closeApModal();
                    }}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div style={S.modalOverlay} onClick={closeApModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{apAppliance.label}</h3>
                  <div style={S.modalSub}>Pick a work type{needsSubChoice ? ", service" : ""} and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeApModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt}
                      type="button"
                      style={apWorkType === wt ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => pickApWorkType(wt)}
                    >
                      {apWorkType === wt ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {APPLIANCE_WORK_TYPE_LABELS[wt]}
                    </button>
                  ))}
                </div>

                {apWorkType && apWorkType !== "consultation" && needsSubChoice && (
                  <>
                    <div style={S.ssFieldLabel}>Service</div>
                    <div style={S.ssRatingRow}>
                      {subChoices.map((sub) => (
                        <button
                          key={sub.value}
                          type="button"
                          style={apSubService?.value === sub.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => pickApSubService(sub)}
                        >
                          {apSubService?.value === sub.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {sub.label} <span style={S.ssRatingPrice}>₹{sub.price}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {needsInstallReq && (
                  <>
                    <div style={S.ssFieldLabel}>Install Requirements</div>
                    <div style={S.ssRatingRow}>
                      {(service.installRequirements || []).map((req) => (
                        <button
                          key={req.value}
                          type="button"
                          style={apInstallReq === req.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setApInstallReq(req.value)}
                        >
                          {apInstallReq === req.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {req.label} {req.extra > 0 && <span style={S.ssRatingPrice}>+₹{req.extra}</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {apSubService && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={apQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setApQty((q) => Math.max(1, q - 1))}
                        disabled={apQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{apQty}</div>
                      <button
                        type="button"
                        style={apQty >= (service.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setApQty((q) => Math.min(service.limit || 10, q + 1))}
                        disabled={apQty >= (service.limit || 10)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmApAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* CCTV — Work Type (Repair/Replace/Install/Remove/Consultation) →
          Place → Area → CCTV Type (camera form factor) → Quantity (1/2/3/4/
          5+, the 5+ revealing a plain stepper to "Add Another") → Setup →
          an optional Photo → Add to Cart. Every field (other than Photo)
          applies the same way regardless of which work type was picked —
          not just gated behind Install like the old liveView-only flow.
          Price = CCTV Type's base price × the chosen work type's
          multiplier, both already defined on the service. */}
      {ccModalOpen && (() => {
        const service = electricalCatalog.find((s) => s.value === "cctv");
        if (!service) return null;
        const workTypes = service.workTypes || [];
        const ccTypes = service.subCategories?.[0]?.types || [];
        const isOver4 = ccCount > 4;
        const canAdd = ccWorkType === "consultation"
          || (Boolean(ccWorkType) && Boolean(ccPlace) && Boolean(ccArea) && Boolean(ccType) && Boolean(ccSetup));
        const previewWorkType = ccWorkType && ccWorkType !== "consultation" ? workTypes.find((w) => w.key === ccWorkType) : null;
        const previewPrice = previewWorkType && ccType ? Math.round(ccType.price * previewWorkType.multiplier) : null;

        const handleCcPhoto = (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => setCcPhoto(String(reader.result || ""));
          reader.readAsDataURL(file);
          e.target.value = "";
        };

        return (
          <div style={S.modalOverlay} onClick={closeCcModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>CCTV</h3>
                  <div style={S.modalSub}>Pick a work type, place, area, camera type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeCcModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={ccWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => setCcWorkType(wt.key)}
                    >
                      {ccWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    style={ccWorkType === "consultation" ? S.ssRatingBtnActive : S.ssRatingBtn}
                    onClick={() => setCcWorkType("consultation")}
                  >
                    {ccWorkType === "consultation" ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                    Consultation <span style={S.ssRatingPrice}>₹{service.consultation?.price || 99}</span>
                  </button>
                </div>

                {ccWorkType === "consultation" ? (
                  <div style={S.ssConsultationNote}>
                    An electrician will inspect the site and advise before any work starts — no separate
                    place, area, camera type or quantity needed for a consultation visit.
                  </div>
                ) : ccWorkType && (
                  <>
                    <div style={S.ssFieldLabel}>Place</div>
                    <div style={S.ssRatingRow}>
                      {(service.places || []).map((p) => (
                        <button
                          key={p.value}
                          type="button"
                          style={ccPlace === p.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setCcPlace(p.value)}
                        >
                          {ccPlace === p.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {p.label}
                        </button>
                      ))}
                    </div>

                    <div style={S.ssFieldLabel}>Area</div>
                    <div style={S.ssRatingRow}>
                      {(service.areas || []).map((a) => (
                        <button
                          key={a.value}
                          type="button"
                          style={ccArea === a.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setCcArea(a.value)}
                        >
                          {ccArea === a.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {a.label}
                        </button>
                      ))}
                    </div>

                    <div style={S.ssFieldLabel}>CCTV Type</div>
                    <div style={S.ssRatingRow}>
                      {ccTypes.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          style={ccType?.value === t.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setCcType(t)}
                        >
                          {ccType?.value === t.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {t.label}
                        </button>
                      ))}
                    </div>

                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssRatingRow}>
                      {(service.cameraCountOptions || [1, 2, 3, 4]).map((n) => (
                        <button
                          key={n}
                          type="button"
                          style={ccCount === n ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setCcCount(n)}
                        >
                          {ccCount === n ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {n}
                        </button>
                      ))}
                      <button
                        type="button"
                        style={isOver4 ? S.ssRatingBtnActive : S.ssRatingBtn}
                        onClick={() => setCcCount((q) => (q > 4 ? q : 5))}
                      >
                        {isOver4 ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                        5+
                      </button>
                    </div>
                    {isOver4 && (
                      <div style={S.ssQtyRow}>
                        <button
                          type="button"
                          style={ccCount <= 5 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                          onClick={() => setCcCount((q) => Math.max(5, q - 1))}
                          disabled={ccCount <= 5}
                        >
                          <Minus size={13} />
                        </button>
                        <div style={S.qtyValueText}>{ccCount}</div>
                        <button
                          type="button"
                          style={ccCount >= (service.limit || 20) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                          onClick={() => setCcCount((q) => Math.min(service.limit || 20, q + 1))}
                          disabled={ccCount >= (service.limit || 20)}
                          aria-label="Add Another"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    )}

                    <div style={S.ssFieldLabel}>Setup</div>
                    <div style={S.ssRatingRow}>
                      {(service.setupOptions || []).map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          style={ccSetup === s.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setCcSetup(s.value)}
                        >
                          {ccSetup === s.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {s.label}
                        </button>
                      ))}
                    </div>

                    <div style={S.ssFieldLabel}>Photo (optional)</div>
                    {ccPhoto ? (
                      <img src={ccPhoto} alt="Site photo" style={S.ssDetailsPhoto} />
                    ) : (
                      <input type="file" accept="image/*" onChange={handleCcPhoto} />
                    )}

                    {previewPrice != null && (
                      <div style={S.ssConsultationNote}>
                        {ccCount} × ₹{previewPrice} = ₹{previewPrice * ccCount}
                      </div>
                    )}
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmCcAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Doorbell Security — Work Type (Repair/Replace/Install/Remove/
          Consultation) → Device Type (within the clicked section) →
          Material (Install/Replace only) → Existing Connection → Issue
          (Repair only, optional) → Quantity → optional Photo → optional
          Notes (required for Consultation) → Add to Cart. A device flagged
          "Other / Not Sure" routes straight to the flat consultation
          charge, same as every other category's shortcut. */}
      {dbModalOpen && dbGroup && (() => {
        const service = electricalCatalog.find((s) => s.value === "doorbell-security");
        if (!service) return null;
        const workTypes = service.workTypes || [];
        const devices = dbGroup.types || [];
        const isConsultation = dbWorkType === "consultation";
        const needsMaterial = dbWorkType === "install" || dbWorkType === "replace";
        const canAdd = isConsultation
          ? Boolean(dbNotes.trim())
          : Boolean(dbWorkType) && Boolean(dbDevice) && (
              dbDevice?.notSure || (Boolean(dbConnection) && (!needsMaterial || Boolean(dbMaterial)))
            );

        const handleDbPhoto = (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => setDbPhoto(String(reader.result || ""));
          reader.readAsDataURL(file);
          e.target.value = "";
        };

        return (
          <div style={S.modalOverlay} onClick={closeDbModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{dbGroup.label}</h3>
                  <div style={S.modalSub}>Select your requirement and device details</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeDbModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={dbWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => { setDbWorkType(wt.key); setDbMaterial(null); setDbIssue(null); }}
                    >
                      {dbWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    style={isConsultation ? S.ssRatingBtnActive : S.ssRatingBtn}
                    onClick={() => { setDbWorkType("consultation"); setDbMaterial(null); setDbIssue(null); }}
                  >
                    {isConsultation ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                    Consultation / Not Sure <span style={S.ssRatingPrice}>₹{service.consultation?.price || 99}</span>
                  </button>
                </div>

                {dbWorkType && (
                  <>
                    <div style={S.ssFieldLabel}>Device Type{isConsultation ? " (optional)" : ""}</div>
                    <div style={S.ssRatingRow}>
                      {devices.map((d) => (
                        <button
                          key={d.value}
                          type="button"
                          style={dbDevice?.value === d.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setDbDevice(d)}
                        >
                          {dbDevice?.value === d.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {d.label}
                          {!d.notSure && !isConsultation && (
                            <span style={S.ssRatingPrice}>₹{Math.round(d.price * (workTypes.find((w) => w.key === dbWorkType)?.multiplier || 1))}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {!isConsultation && dbDevice && !dbDevice.notSure && needsMaterial && (
                  <>
                    <div style={S.ssFieldLabel}>Material</div>
                    <div style={S.ssRatingRow}>
                      {(service.materialOptions || []).map((m) => (
                        <button
                          key={m.value}
                          type="button"
                          style={dbMaterial === m.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setDbMaterial(m.value)}
                        >
                          {dbMaterial === m.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {!isConsultation && dbDevice && !dbDevice.notSure && (
                  <>
                    <div style={S.ssFieldLabel}>Existing Connection</div>
                    <div style={S.ssRatingRow}>
                      {(service.connectionOptions || []).map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          style={dbConnection === c.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setDbConnection(c.value)}
                        >
                          {dbConnection === c.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {c.label}
                        </button>
                      ))}
                    </div>
                    <div style={S.ssConsultationNote}>
                      An electrician will verify the electrical connection and installation requirements.
                    </div>
                  </>
                )}

                {!isConsultation && dbWorkType === "repair" && dbDevice && !dbDevice.notSure && (
                  <>
                    <div style={S.ssFieldLabel}>Issue (optional)</div>
                    <div style={S.ssRatingRow}>
                      {(service.issueOptions || []).map((i) => (
                        <button
                          key={i.value}
                          type="button"
                          style={dbIssue === i.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setDbIssue((cur) => (cur === i.value ? null : i.value))}
                        >
                          {dbIssue === i.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {i.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {!isConsultation && dbDevice && !dbDevice.notSure && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={dbQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setDbQty((q) => Math.max(1, q - 1))}
                        disabled={dbQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{dbQty}</div>
                      <button
                        type="button"
                        style={dbQty >= (service.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setDbQty((q) => Math.min(service.limit || 10, q + 1))}
                        disabled={dbQty >= (service.limit || 10)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                {dbWorkType && (
                  <>
                    <div style={S.ssFieldLabel}>Photo (optional)</div>
                    {dbPhoto ? (
                      <img src={dbPhoto} alt="Device photo" style={S.ssDetailsPhoto} />
                    ) : (
                      <input type="file" accept="image/*" onChange={handleDbPhoto} />
                    )}

                    <div style={S.ssFieldLabel}>
                      {isConsultation ? "Describe the issue or installation requirement" : "Additional Notes (optional)"}
                    </div>
                    <textarea
                      style={S.ssNumberInput}
                      rows={3}
                      value={dbNotes}
                      onChange={(e) => setDbNotes(e.target.value)}
                      maxLength={500}
                      placeholder={isConsultation
                        ? "Doorbell not ringing, existing wiring available, install near front gate..."
                        : "Anything else the electrician should know?"}
                    />
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmDbAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Stabilizer — work type (Repair/Replace/Install/Remove, each
          priced live by appliance base price × multiplier) + a synthetic
          Consultation option → quantity → Add to Cart. A device flagged
          `notSure` skips straight to a flat consultation-only panel. */}
      {stModalOpen && (() => {
        const service = electricalCatalog.find((s) => s.value === "stabilizer");
        if (!service) return null;
        const devices = service.subCategories?.[0]?.types || [];

        // Step 1 — Appliance (moved inside the popup; the center list is
        // now a single consolidated card, not one row per appliance).
        if (!stDevice) {
          return (
            <div style={S.modalOverlay} onClick={closeStModal}>
              <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.modalHeader}>
                  <div>
                    <h3 style={S.modalTitle}>Stabilizer</h3>
                    <div style={S.modalSub}>What's the stabiliser for?</div>
                  </div>
                  <button type="button" style={S.modalCloseBtn} onClick={closeStModal}>
                    <X size={18} />
                  </button>
                </div>
                <div style={S.modalBody}>
                  <div style={S.ssFieldLabel}>Appliance</div>
                  <div style={S.ssRatingRow}>
                    {devices.map((device) => (
                      <button
                        key={device.value}
                        type="button"
                        style={S.ssRatingBtn}
                        onClick={() => setStDevice(device)}
                      >
                        <Circle size={14} />
                        {device.label}
                        {!device.notSure && <span style={S.ssRatingPrice}>₹{device.price}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        if (stDevice.notSure) {
          return (
            <div style={S.modalOverlay} onClick={closeStModal}>
              <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.modalHeader}>
                  <div>
                    <h3 style={S.modalTitle}>{stDevice.label}</h3>
                    <div style={S.modalSub}>Flat consultation charge — ₹{service.consultation?.price || 99}</div>
                  </div>
                  <button type="button" style={S.modalCloseBtn} onClick={closeStModal}>
                    <X size={18} />
                  </button>
                </div>
                <div style={S.modalBody}>
                  <button type="button" style={S.ssOptionDetailsLink} onClick={() => setStDevice(null)}>
                    ‹ Change appliance
                  </button>
                  <div style={S.ssConsultationNote}>
                    An electrician will inspect the stabiliser on-site and advise before any work starts.
                  </div>
                  <button type="button" style={S.ssAddBtn} onClick={confirmStAdd}>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          );
        }

        const workTypes = service.workTypes || [];
        const canAdd = Boolean(stWorkType);

        return (
          <div style={S.modalOverlay} onClick={closeStModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{stDevice.label}</h3>
                  <div style={S.modalSub}>Pick a work type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeStModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <button type="button" style={S.ssOptionDetailsLink} onClick={() => setStDevice(null)}>
                  ‹ Change appliance
                </button>

                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={stWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => setStWorkType(wt.key)}
                    >
                      {stWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label} <span style={S.ssRatingPrice}>₹{Math.round(stDevice.price * wt.multiplier)}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    style={stWorkType === "consultation" ? S.ssRatingBtnActive : S.ssRatingBtn}
                    onClick={() => setStWorkType("consultation")}
                  >
                    {stWorkType === "consultation" ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                    Consultation <span style={S.ssRatingPrice}>₹{service.consultation?.price || 99}</span>
                  </button>
                </div>

                {stWorkType && stWorkType !== "consultation" && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={stQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setStQty((q) => Math.max(1, q - 1))}
                        disabled={stQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{stQty}</div>
                      <button
                        type="button"
                        style={stQty >= (service.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setStQty((q) => Math.min(service.limit || 10, q + 1))}
                        disabled={stQty >= (service.limit || 10)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmStAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Submeter & Water Sensor — work type × multiplier + synthetic
          Consultation → quantity → Add to Cart. Same shape as Doorbell. */}
      {smModalOpen && smDevice && (() => {
        const service = electricalCatalog.find((s) => s.value === "submeter-water-sensor");
        if (!service) return null;
        const workTypes = service.workTypes || [];
        const canAdd = Boolean(smWorkType);

        return (
          <div style={S.modalOverlay} onClick={closeSmModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{smDevice.label}</h3>
                  <div style={S.modalSub}>Pick a work type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeSmModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={smWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => setSmWorkType(wt.key)}
                    >
                      {smWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label} <span style={S.ssRatingPrice}>₹{Math.round(smDevice.price * wt.multiplier)}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    style={smWorkType === "consultation" ? S.ssRatingBtnActive : S.ssRatingBtn}
                    onClick={() => setSmWorkType("consultation")}
                  >
                    {smWorkType === "consultation" ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                    Consultation <span style={S.ssRatingPrice}>₹{service.consultation?.price || 99}</span>
                  </button>
                </div>

                {smWorkType && smWorkType !== "consultation" && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={smQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setSmQty((q) => Math.max(1, q - 1))}
                        disabled={smQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{smQty}</div>
                      <button
                        type="button"
                        style={smQty >= (service.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setSmQty((q) => Math.min(service.limit || 10, q + 1))}
                        disabled={smQty >= (service.limit || 10)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmSmAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Switchboard — work type (Repair/Replace/Install/Remove/Not Sure,
          each priced live by points-tier base price × multiplier) →
          quantity → Add to Cart. No separate consultation object — "Not
          Sure" is already one of the 5 work types. */}
      {sbModalOpen && sbDevice && (() => {
        const service = electricalCatalog.find((s) => s.value === "switchboard");
        if (!service) return null;
        const workTypes = service.workTypes || [];
        const canAdd = Boolean(sbWorkType);
        const deviceLabel = ["switchboard_other", "switchboard_notsure"].includes(sbDevice.value)
          ? sbDevice.label
          : `${sbDevice.label}-Point Switchboard`;

        return (
          <div style={S.modalOverlay} onClick={closeSbModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{deviceLabel}</h3>
                  <div style={S.modalSub}>Pick a work type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeSbModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={sbWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => setSbWorkType(wt.key)}
                    >
                      {sbWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label} <span style={S.ssRatingPrice}>₹{Math.round(sbDevice.price * wt.multiplier)}</span>
                    </button>
                  ))}
                </div>

                {sbWorkType && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={sbQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setSbQty((q) => Math.max(1, q - 1))}
                        disabled={sbQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{sbQty}</div>
                      <button
                        type="button"
                        style={sbQty >= (sbDevice.limit || service.limit || 1) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setSbQty((q) => Math.min(sbDevice.limit || service.limit || 1, q + 1))}
                        disabled={sbQty >= (sbDevice.limit || service.limit || 1)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmSbAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Wiring — Work Type (What Do You Need?) → Wiring Type (the only
          priced dimension) → Purpose (What Is It For?) → Approximate
          Length → Existing Connection → Quantity → optional Photo → Add to
          Cart, per the supplied Customer Booking Process spec.
          Consultation (from Step 1 or a "Not Sure" Wiring Type) skips
          straight to a requirement-description panel. */}
      {wrModalOpen && (() => {
        const service = electricalCatalog.find((s) => s.value === "wiring");
        if (!service) return null;
        const workTypes = service.workTypes || [];
        const types = service.subCategories?.[0]?.types || [];
        const isConsultation = wrWorkType === "consultation" || wrType?.notSure;
        const canAdd = isConsultation
          ? Boolean(wrNotes.trim())
          : Boolean(wrWorkType) && Boolean(wrType) && Boolean(wrPurpose) && Boolean(wrLengthOption) && Boolean(wrConnection);

        const handleWrPhoto = (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => setWrPhoto(String(reader.result || ""));
          reader.readAsDataURL(file);
          e.target.value = "";
        };

        return (
          <div style={S.modalOverlay} onClick={closeWrModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>Wiring</h3>
                  <div style={S.modalSub}>What do you need?</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeWrModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={wrWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => { setWrWorkType(wt.key); setWrType(null); }}
                    >
                      {wrWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    style={wrWorkType === "consultation" ? S.ssRatingBtnActive : S.ssRatingBtn}
                    onClick={() => { setWrWorkType("consultation"); setWrType(null); }}
                  >
                    {wrWorkType === "consultation" ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                    Consultation / Not Sure <span style={S.ssRatingPrice}>₹{service.consultation?.price || 99}</span>
                  </button>
                </div>

                {wrWorkType && wrWorkType !== "consultation" && (
                  <>
                    <div style={S.ssFieldLabel}>Select Wiring Type</div>
                    <div style={S.ssRatingRow}>
                      {types.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          style={wrType?.value === t.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setWrType(t)}
                        >
                          {wrType?.value === t.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {t.label}
                          {!t.notSure && <span style={S.ssRatingPrice}>₹{t.price}</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {isConsultation ? (
                  wrWorkType && (
                    <>
                      <div style={S.ssFieldLabel}>Describe the wiring requirement</div>
                      <textarea
                        style={S.ssNumberInput}
                        rows={3}
                        value={wrNotes}
                        onChange={(e) => setWrNotes(e.target.value)}
                        maxLength={500}
                        placeholder="e.g. Not sure if this needs new wiring or just a repair, exposed wire near the kitchen switchboard..."
                      />
                      <div style={S.ssFieldLabel}>Photo (optional)</div>
                      {wrPhoto ? (
                        <img src={wrPhoto} alt="Site photo" style={S.ssDetailsPhoto} />
                      ) : (
                        <input type="file" accept="image/*" onChange={handleWrPhoto} />
                      )}
                    </>
                  )
                ) : (
                  wrType && !wrType.notSure && (
                    <>
                      <div style={S.ssFieldLabel}>What Is the Wiring For?</div>
                      <div style={S.ssRatingRow}>
                        {(service.purposes || []).map((p) => (
                          <button
                            key={p.value}
                            type="button"
                            style={wrPurpose === p.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                            onClick={() => setWrPurpose(p.value)}
                          >
                            {wrPurpose === p.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                            {p.label}
                          </button>
                        ))}
                      </div>

                      {wrPurpose && (
                        <>
                          <div style={S.ssFieldLabel}>Approximate Length</div>
                          <div style={S.ssRatingRow}>
                            {(service.lengthOptions || []).map((l) => (
                              <button
                                key={l.value}
                                type="button"
                                style={wrLengthOption === l.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                                onClick={() => setWrLengthOption(l.value)}
                              >
                                {wrLengthOption === l.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                                {l.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {wrLengthOption && (
                        <>
                          <div style={S.ssFieldLabel}>Existing Connection</div>
                          <div style={S.ssRatingRow}>
                            {(service.connectionOptions || []).map((c) => (
                              <button
                                key={c.value}
                                type="button"
                                style={wrConnection === c.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                                onClick={() => setWrConnection(c.value)}
                              >
                                {wrConnection === c.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                                {c.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {wrConnection && (
                        <>
                          <div style={S.ssFieldLabel}>Quantity</div>
                          <div style={S.ssQtyRow}>
                            <button
                              type="button"
                              style={wrQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                              onClick={() => setWrQty((q) => Math.max(1, q - 1))}
                              disabled={wrQty <= 1}
                            >
                              <Minus size={13} />
                            </button>
                            <div style={S.qtyValueText}>{wrQty}</div>
                            <button
                              type="button"
                              style={wrQty >= (wrType.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                              onClick={() => setWrQty((q) => Math.min(wrType.limit || 10, q + 1))}
                              disabled={wrQty >= (wrType.limit || 10)}
                            >
                              <Plus size={13} />
                            </button>
                          </div>

                          <div style={S.ssFieldLabel}>Photo (optional)</div>
                          {wrPhoto ? (
                            <img src={wrPhoto} alt="Site photo" style={S.ssDetailsPhoto} />
                          ) : (
                            <input type="file" accept="image/*" onChange={handleWrPhoto} />
                          )}
                        </>
                      )}
                    </>
                  )
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmWrAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MCB/Fuse — Work Type first → Type within the clicked group →
          quantity → Add to Cart. The "Other" group has no structured
          types at all, so it short-circuits to a flat consultation
          panel. */}
      {mfModalOpen && mfGroup && (() => {
        const service = electricalCatalog.find((s) => s.value === "mcb-fuse");
        if (!service) return null;

        if ((mfGroup.types || []).length === 0) {
          return (
            <div style={S.modalOverlay} onClick={closeMfModal}>
              <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.modalHeader}>
                  <div>
                    <h3 style={S.modalTitle}>{mfGroup.label}</h3>
                    <div style={S.modalSub}>Flat consultation charge — ₹{service.consultation?.price || 99}</div>
                  </div>
                  <button type="button" style={S.modalCloseBtn} onClick={closeMfModal}>
                    <X size={18} />
                  </button>
                </div>
                <div style={S.modalBody}>
                  <div style={S.ssConsultationNote}>
                    A technician will inspect the MCB/fuse panel on-site and advise before any work starts.
                  </div>
                  <button type="button" style={S.ssAddBtn} onClick={confirmMfAdd}>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          );
        }

        const workTypes = service.workTypes || [];
        const types = mfGroup.types || [];
        const canAdd = Boolean(mfWorkType) && Boolean(mfType);

        return (
          <div style={S.modalOverlay} onClick={closeMfModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{mfGroup.label}</h3>
                  <div style={S.modalSub}>Pick a work type, type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeMfModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={mfWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => pickMfWorkType(wt.key)}
                    >
                      {mfWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label}
                    </button>
                  ))}
                </div>

                {mfWorkType && (
                  <>
                    <div style={S.ssFieldLabel}>Type</div>
                    <div style={S.ssRatingRow}>
                      {types.map((t) => {
                        const wt = workTypes.find((w) => w.key === mfWorkType);
                        return (
                          <button
                            key={t.value}
                            type="button"
                            style={mfType?.value === t.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                            onClick={() => setMfType(t)}
                          >
                            {mfType?.value === t.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                            {t.label} <span style={S.ssRatingPrice}>₹{Math.round(t.price * (wt?.multiplier || 1))}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {mfType && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={mfQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setMfQty((q) => Math.max(1, q - 1))}
                        disabled={mfQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{mfQty}</div>
                      <button
                        type="button"
                        style={mfQty >= (mfType.limit || service.limit || 20) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setMfQty((q) => Math.min(mfType.limit || service.limit || 20, q + 1))}
                        disabled={mfQty >= (mfType.limit || service.limit || 20)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmMfAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Light — same Work Type → Type-within-group → quantity shape as
          MCB/Fuse. The "Other" group has no structured types, so it
          short-circuits to a flat consultation panel. */}
      {ltModalOpen && ltGroup && (() => {
        const service = electricalCatalog.find((s) => s.value === "light");
        if (!service) return null;

        if ((ltGroup.types || []).length === 0) {
          return (
            <div style={S.modalOverlay} onClick={closeLtModal}>
              <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.modalHeader}>
                  <div>
                    <h3 style={S.modalTitle}>{ltGroup.label} Light</h3>
                    <div style={S.modalSub}>Flat consultation charge — ₹{service.consultation?.price || 99}</div>
                  </div>
                  <button type="button" style={S.modalCloseBtn} onClick={closeLtModal}>
                    <X size={18} />
                  </button>
                </div>
                <div style={S.modalBody}>
                  <div style={S.ssConsultationNote}>
                    An electrician will inspect the light fixture on-site and advise before any work starts.
                  </div>
                  <button type="button" style={S.ssAddBtn} onClick={confirmLtAdd}>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          );
        }

        const workTypes = service.workTypes || [];
        const types = ltGroup.types || [];
        const canAdd = Boolean(ltWorkType) && Boolean(ltType);

        return (
          <div style={S.modalOverlay} onClick={closeLtModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{ltGroup.label} Light</h3>
                  <div style={S.modalSub}>Pick a work type, type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeLtModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {workTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={ltWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => pickLtWorkType(wt.key)}
                    >
                      {ltWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label}
                    </button>
                  ))}
                </div>

                {ltWorkType && (
                  <>
                    <div style={S.ssFieldLabel}>Type</div>
                    <div style={S.ssRatingRow}>
                      {types.map((t) => {
                        const wt = workTypes.find((w) => w.key === ltWorkType);
                        return (
                          <button
                            key={t.value}
                            type="button"
                            style={ltType?.value === t.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                            onClick={() => setLtType(t)}
                          >
                            {ltType?.value === t.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                            {t.label} <span style={S.ssRatingPrice}>{t.startingPrice ? "from " : ""}₹{Math.round(t.price * (wt?.multiplier || 1))}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {ltType && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={ltQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setLtQty((q) => Math.max(1, q - 1))}
                        disabled={ltQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{ltQty}</div>
                      <button
                        type="button"
                        style={ltQty >= (ltType.limit || service.limit || 20) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setLtQty((q) => Math.min(ltType.limit || service.limit || 20, q + 1))}
                        disabled={ltQty >= (ltType.limit || service.limit || 20)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmLtAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Inverter UPS — Work Type (from component.exactWork) → New
          Installation options (only when the chosen work is flagged
          isInstall — REPLACES the price, unlike Appliance's
          installRequirements which only adds an extra amount) → quantity
          → Add to Cart. "Other" has no structured exactWork, so it
          short-circuits to a flat consultation panel. */}
      {ivModalOpen && ivComponent && (() => {
        const service = electricalCatalog.find((s) => s.value === "inverter-ups");
        if (!service) return null;

        if ((ivComponent.exactWork || []).length === 0) {
          return (
            <div style={S.modalOverlay} onClick={closeIvModal}>
              <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.modalHeader}>
                  <div>
                    <h3 style={S.modalTitle}>{ivComponent.label}</h3>
                    <div style={S.modalSub}>Flat consultation charge — ₹{service.consultation?.price || 99}</div>
                  </div>
                  <button type="button" style={S.modalCloseBtn} onClick={closeIvModal}>
                    <X size={18} />
                  </button>
                </div>
                <div style={S.modalBody}>
                  <div style={S.ssConsultationNote}>
                    An electrician will inspect on-site and advise before any work starts.
                  </div>
                  <button type="button" style={S.ssAddBtn} onClick={confirmIvAdd}>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          );
        }

        const work = (ivComponent.exactWork || []).find((w) => w.value === ivWorkType);
        const needsInstallOpt = Boolean(work?.isInstall);
        const canAdd = Boolean(work) && (!needsInstallOpt || Boolean(ivInstallOpt));

        return (
          <div style={S.modalOverlay} onClick={closeIvModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{ivComponent.label}</h3>
                  <div style={S.modalSub}>Pick a work type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeIvModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {(ivComponent.exactWork || []).map((w) => (
                    <button
                      key={w.value}
                      type="button"
                      style={ivWorkType === w.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => pickIvWorkType(w)}
                    >
                      {ivWorkType === w.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {w.label} {!w.isInstall && <span style={S.ssRatingPrice}>₹{w.price}</span>}
                    </button>
                  ))}
                </div>

                {needsInstallOpt && (
                  <>
                    <div style={S.ssFieldLabel}>New Installation</div>
                    <div style={S.ssRatingRow}>
                      {(service.newInstallationOptions || []).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          style={ivInstallOpt === opt.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setIvInstallOpt(opt.value)}
                        >
                          {ivInstallOpt === opt.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {opt.label} <span style={S.ssRatingPrice}>₹{opt.price}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {work && (!needsInstallOpt || ivInstallOpt) && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={ivQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setIvQty((q) => Math.max(1, q - 1))}
                        disabled={ivQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{ivQty}</div>
                      <button
                        type="button"
                        style={ivQty >= (service.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setIvQty((q) => Math.min(service.limit || 10, q + 1))}
                        disabled={ivQty >= (service.limit || 10)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmIvAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Fan — Work Type (Repair/Install/Remove/Consultation) → Type
          within the clicked category (e.g. "Normal Ceiling Fan", "BLDC
          Fan" for the Ceiling Fan category) — each (workType,category,
          type) combo already carries its own absolute price, no
          multiplier math needed → quantity → Add to Cart. */}
      {fnModalOpen && fnCategory && (() => {
        const service = electricalCatalog.find((s) => s.value === "fan");
        if (!service) return null;
        const realWorkTypes = (service.workTypes || []).filter((w) => w.key !== "consultation");
        const types = fnWorkType && fnWorkType !== "consultation" ? fnTypesFor(fnWorkType, fnCategory) : [];
        const canAdd = fnWorkType === "consultation" || Boolean(fnType);

        return (
          <div style={S.modalOverlay} onClick={closeFnModal}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div>
                  <h3 style={S.modalTitle}>{fnCategory}</h3>
                  <div style={S.modalSub}>Pick a work type, type and quantity</div>
                </div>
                <button type="button" style={S.modalCloseBtn} onClick={closeFnModal}>
                  <X size={18} />
                </button>
              </div>

              <div style={S.modalBody}>
                <div style={S.ssFieldLabel}>Work Type</div>
                <div style={S.ssRatingRow}>
                  {realWorkTypes.map((wt) => (
                    <button
                      key={wt.key}
                      type="button"
                      style={fnWorkType === wt.key ? S.ssRatingBtnActive : S.ssRatingBtn}
                      onClick={() => pickFnWorkType(wt.key)}
                    >
                      {fnWorkType === wt.key ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      {wt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    style={fnWorkType === "consultation" ? S.ssRatingBtnActive : S.ssRatingBtn}
                    onClick={() => pickFnWorkType("consultation")}
                  >
                    {fnWorkType === "consultation" ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                    Consultation <span style={S.ssRatingPrice}>₹{(service.workTypes || []).find((w) => w.key === "consultation")?.flat?.price || 99}</span>
                  </button>
                </div>

                {fnWorkType && fnWorkType !== "consultation" && (
                  <>
                    <div style={S.ssFieldLabel}>Type</div>
                    <div style={S.ssRatingRow}>
                      {types.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          style={fnType?.value === t.value ? S.ssRatingBtnActive : S.ssRatingBtn}
                          onClick={() => setFnType(t)}
                        >
                          {fnType?.value === t.value ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          {t.label} <span style={S.ssRatingPrice}>₹{t.price}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {fnType && (
                  <>
                    <div style={S.ssFieldLabel}>Quantity</div>
                    <div style={S.ssQtyRow}>
                      <button
                        type="button"
                        style={fnQty <= 1 ? S.elecQtyBtnDisabled : S.elecQtyBtn}
                        onClick={() => setFnQty((q) => Math.max(1, q - 1))}
                        disabled={fnQty <= 1}
                      >
                        <Minus size={13} />
                      </button>
                      <div style={S.qtyValueText}>{fnQty}</div>
                      <button
                        type="button"
                        style={fnQty >= (fnType.limit || service.limit || 10) ? S.elecQtyBtnDisabled : S.elecQtyBtnP}
                        onClick={() => setFnQty((q) => Math.min(fnType.limit || service.limit || 10, q + 1))}
                        disabled={fnQty >= (fnType.limit || service.limit || 10)}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  style={canAdd ? S.ssAddBtn : S.ssAddBtnDisabled}
                  onClick={confirmFnAdd}
                  disabled={!canAdd}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1300px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px" },
  breadcrumbLink: { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  hero:      { textAlign: "center", maxWidth: "620px", margin: "0 auto 26px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#16a34a", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "30px", fontWeight: 800, letterSpacing: "-0.5px", color: "#111827" },
  heroTitleAccent: { color: "#16a34a" },
  heroText:  { margin: "10px 0 0", color: "#6b7280", fontSize: "14px", lineHeight: 1.6 },

  heroSplit: { display: "grid", gridTemplateColumns: "1.5fr 0.72fr", gap: "32px", alignItems: "center", marginBottom: "30px" },
  heroLeft:  { maxWidth: "460px" },
  heroRight: { position: "relative", maxWidth: "360px", marginLeft: "auto" },
  heroImg:   { width: "100%", maxHeight: "230px", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: "22px", display: "block", boxShadow: "0 16px 40px rgba(15,23,42,0.14)" },
  heroBadgeCard: { position: "absolute", bottom: "-18px", right: "-14px", background: "#fff", borderRadius: "16px", padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", boxShadow: "0 12px 30px rgba(15,23,42,0.16)" },
  heroBadgeCardIcon: { width: "38px", height: "38px", borderRadius: "10px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  heroBadgeCardSub:  { fontSize: "11px", color: "#9ca3af", fontWeight: 600, lineHeight: 1.3 },
  heroBadgeCardMain: { fontSize: "17px", fontWeight: 800, color: "#111827", lineHeight: 1.3 },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 28px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#16a34a", color: "#fff" },
  stepCircleDone: { background: "#bbf7d0", color: "#16a34a" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af", marginTop: "1px" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#16a34a" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  sectionTitle: { margin: "0 0 16px", fontSize: "20px", fontWeight: 800, color: "#111827" },

  categoryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px", marginBottom: "28px" },
  categoryCard: { position: "relative", background: "#fff", border: "1px solid #ececec", borderRadius: "18px", overflow: "hidden", textAlign: "left", boxShadow: "0 6px 20px rgba(15,23,42,0.05)" },
  categoryImg:  { width: "100%", aspectRatio: "16 / 10", objectFit: "cover", display: "block" },
  // Small badge on every card's image, top-left — same pattern as the
  // "⚡ Instant" badge on the Instant Home Services catalog page.
  scheduleBadge: { position: "absolute", top: "8px", left: "8px", background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe", fontSize: "10px", fontWeight: 800, padding: "2px 8px", borderRadius: "999px", zIndex: 1, display: "flex", alignItems: "center", gap: "4px" },
  categoryBody: { padding: "18px 20px 20px" },
  categoryLabel: { fontSize: "17px", fontWeight: 800, color: "#111827", marginBottom: "12px" },
  categoryPriceRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" },
  categoryPricePill: { fontSize: "12px", fontWeight: 700, color: "#16a34a", background: "#dcfce7", padding: "6px 12px", borderRadius: "999px" },
  categoryCount: { fontSize: "12px", color: "#9ca3af", marginBottom: "16px" },
  categoryCtaSolid:   { width: "100%", padding: "12px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", border: "none", fontSize: "14px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  categoryCtaOutline: { width: "100%", padding: "12px 0", borderRadius: "12px", background: "#fff", color: "#16a34a", border: "1.5px solid #16a34a", fontSize: "14px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },

  trustStrip: { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "18px", padding: "22px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" },
  trustItem: { display: "flex", alignItems: "flex-start", gap: "12px" },
  trustIconWrap: { width: "40px", height: "40px", borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  trustTitle: { fontSize: "13px", fontWeight: 800, color: "#111827" },
  trustText:  { fontSize: "12px", color: "#6b7280", marginTop: "2px", lineHeight: 1.4 },

  serviceBanner: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "16px", padding: "20px 24px", marginBottom: "22px" },
  serviceBannerLeft: { display: "flex", alignItems: "center", gap: "14px" },
  serviceBannerIconWrap: { width: "44px", height: "44px", borderRadius: "50%", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  serviceBannerTitle: { fontSize: "17px", fontWeight: 800, color: "#111827" },
  serviceBannerText:  { fontSize: "13px", color: "#6b7280", marginTop: "3px" },

  serviceGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px", marginBottom: "22px" },
  serviceCard: { position: "relative", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", cursor: "pointer", padding: 0, textAlign: "left" },
  serviceCardActive: { border: "1.5px solid #16a34a", boxShadow: "0 0 0 3px rgba(22,163,74,0.12)" },
  serviceImg:  { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" },
  serviceBody: { padding: "8px 10px" },
  serviceCheckRow: { display: "flex", alignItems: "center", gap: "6px" },
  serviceName: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  serviceCategory: { fontSize: "12px", color: "#6b7280", marginTop: "3px" },

  servicesFooterBar: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "18px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "16px", padding: "18px 22px" },
  servicesFooterLeft: { display: "flex", alignItems: "center", gap: "14px" },
  servicesFooterIconWrap: { width: "44px", height: "44px", borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  servicesFooterTitle: { fontSize: "14px", fontWeight: 800, color: "#111827" },
  servicesFooterText:  { fontSize: "12px", color: "#6b7280", marginTop: "2px" },
  servicesFooterBtn: { width: "auto", padding: "13px 28px", marginTop: 0 },

  // Sentinel at the bottom of Home Cleaning's 21-item showcase — an
  // IntersectionObserver watches this div; once it scrolls into view,
  // Continue unlocks (see homeCleaningReady).
  scrollSentinel: { textAlign: "center", padding: "18px", fontSize: "13px", color: "#9ca3af", marginBottom: "6px" },
  scrollSentinelDone: { display: "inline-flex", alignItems: "center", gap: "6px", color: "#15803d", fontWeight: 700 },

  // Grid, not flex, with an explicit minmax(0, 1fr) middle track — the
  // robust fix for a flex/grid item that needs to both grow AND be
  // allowed to shrink/wrap below its content's natural width; without
  // it the middle column refuses to shrink and the whole row either
  // overflows or (as happened here) wraps one word per line instead.
  summaryBar: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 20px", marginBottom: "20px", display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", alignItems: "center", gap: "16px" },
  summaryImg: { width: "64px", height: "64px", borderRadius: "12px", objectFit: "cover", display: "block" },
  summaryBody: { minWidth: 0 },
  summaryTitle: { fontSize: "16px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  summaryChipRow: { display: "flex", flexWrap: "wrap", gap: "6px" },
  summaryChip: { fontSize: "12px", fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryDurationChip: { fontSize: "12px", fontWeight: 700, color: "#fff", background: "#16a34a", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryChangeBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "9px 16px", borderRadius: "10px", whiteSpace: "nowrap" },

  scheduleHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "18px" },
  scheduleHeadSub: { margin: "4px 0 0", fontSize: "13px", color: "#6b7280" },
  legend: { display: "flex", gap: "16px", flexWrap: "wrap" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" },
  legendDot: { width: "9px", height: "9px", borderRadius: "50%", display: "inline-block" },

  scheduleColumns: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "22px", alignItems: "start" },
  scheduleCol: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  scheduleColTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px" },
  scheduleNote: { display: "flex", alignItems: "flex-start", gap: "8px", background: "#f0fdf4", borderRadius: "10px", padding: "10px 12px", marginTop: "14px", fontSize: "12px", color: "#15803d", lineHeight: 1.4 },

  // Home Cleaning's duration-dependent "What's Covered" panel — re-renders
  // off the selected 1hr/2hr duration (see HOME_CLEANING_SCOPE above).
  scopeSection: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "20px", marginBottom: "22px" },
  scopeHead: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "14px", marginBottom: "18px", flexWrap: "wrap" },
  scopeHeadLeft: { display: "flex", alignItems: "flex-start", gap: "10px" },
  scopeToggle: { display: "flex", gap: "6px", background: "#f3f4f6", borderRadius: "10px", padding: "3px" },
  scopeToggleBtn: { border: "none", background: "transparent", color: "#6b7280", fontSize: "12.5px", fontWeight: 700, padding: "7px 14px", borderRadius: "8px", cursor: "pointer" },
  scopeToggleBtnActive: { background: "#4f46e5", color: "#fff" },
  scopeTitle: { fontSize: "15px", fontWeight: 800, color: "#111827" },
  scopeSub: { fontSize: "12px", color: "#6b7280", marginTop: "2px" },
  scopeGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" },
  scopePanel: { background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "12px", padding: "14px 16px" },
  scopePanelLabel: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", fontWeight: 800, color: "#111827", marginBottom: "10px" },
  scopeList: { margin: 0, padding: "0 0 0 16px", display: "flex", flexDirection: "column", gap: "6px" },
  scopeListItem: { fontSize: "12.5px", color: "#4b5563", lineHeight: 1.5 },
  scopeRoutineNote: { background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: "10px", padding: "10px 14px", fontSize: "12.5px", color: "#3730a3", lineHeight: 1.6, marginBottom: "16px" },
  scopeNotesPanel: { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "12px", padding: "14px 16px" },
  scopeNotesTable: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" },
  scopeNoteRow: { display: "grid", gridTemplateColumns: "140px 1fr", gap: "10px" },
  scopeNoteLabel: { fontSize: "12px", fontWeight: 800, color: "#92400e" },
  scopeNoteText: { fontSize: "12px", color: "#78350f", lineHeight: 1.5 },
  scopeCommonNote: { fontSize: "12px", color: "#78350f", lineHeight: 1.6, fontStyle: "italic", borderTop: "1px solid #fde68a", paddingTop: "10px" },

  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11px", fontWeight: 700, color: "#16a34a" },

  durationList: { display: "flex", flexDirection: "column", gap: "10px" },
  durationRow: { display: "flex", alignItems: "center", gap: "12px", padding: "14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  durationRowActive: { border: "1.5px solid #16a34a", background: "#f0fdf4" },
  durationRowIconWrap: { width: "36px", height: "36px", borderRadius: "10px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  durationRowBody: { flex: 1 },
  durationRowTitle: { fontSize: "14px", fontWeight: 800, color: "#111827" },
  durationRowTagline: { fontSize: "12px", color: "#6b7280", marginTop: "2px" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  stateBox: { padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" },
  errorBox: { padding: "14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },

  // Technical (electrical) itemized catalog — quantity steppers + sub-service modal
  elecPriceText: { fontSize: "13px", fontWeight: 800, color: "#16a34a", margin: "6px 0" },
  elecComingSoon: { fontSize: "12px", fontWeight: 700, color: "#9ca3af", margin: "6px 0", fontStyle: "italic" },

  // Work Type → Category → Type drill-down modal (Fan, and future similar categories)
  workTypeTabRow: { display: "flex", gap: "8px", flexWrap: "wrap", padding: "0 0 14px", borderBottom: "1px solid #eceef5", marginBottom: "14px" },
  workTypeTab: { padding: "8px 16px", borderRadius: "999px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  workTypeTabActive: { padding: "8px 16px", borderRadius: "999px", border: "1.5px solid #16a34a", background: "#16a34a", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  workTypeCategoryBlock: { marginBottom: "6px" },
  workTypeCategoryTitle: { fontSize: "12px", fontWeight: 800, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.02em", margin: "10px 0 8px" },
  elecQtyRow: { display: "flex", alignItems: "center", gap: "8px", marginTop: "14px" },
  elecQtyBtn: { width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #e4e7ef", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280", flexShrink: 0 },
  elecQtyBtnP: { width: "26px", height: "26px", borderRadius: "50%", border: "none", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 },
  elecQtyBtnDisabled: { width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #eceef5", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", cursor: "not-allowed", color: "#c1c5cf", flexShrink: 0 },
  elecQtyText: { fontSize: "12px", fontWeight: 700, color: "#111827" },
  elecSelectBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", width: "100%", height: "38px", borderRadius: "10px", border: "none", background: "#16a34a", color: "#fff", fontSize: "13px", fontWeight: 800, cursor: "pointer" },
  elecSelectBtnActive: { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", width: "100%", height: "38px", borderRadius: "10px", border: "1.5px solid #16a34a", background: "#ecfdf3", color: "#15803d", fontSize: "13px", fontWeight: 800, cursor: "pointer" },
  elecSelectBtnDisabled: { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", width: "100%", height: "38px", borderRadius: "10px", border: "1px solid #eceef5", background: "#f3f4f6", color: "#c1c5cf", fontSize: "13px", fontWeight: 800, cursor: "not-allowed" },

  // ── Electrical Services — sidebar / center-detail / cart-panel redesign ──
  elecLayout: { display: "grid", gridTemplateColumns: "260px 1fr 320px", gap: "20px", alignItems: "start", marginBottom: "22px" },

  elecSidebar: { display: "flex", flexDirection: "column", gap: "6px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "10px" },
  elecSidebarItem: { display: "flex", alignItems: "center", gap: "10px", padding: "11px 12px", borderRadius: "12px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", width: "100%" },
  elecSidebarItemActive: { background: "#ecfdf3", boxShadow: "inset 0 0 0 1.5px #bbf7d0" },
  elecSidebarIcon: { width: "32px", height: "32px", borderRadius: "8px", objectFit: "cover", flexShrink: 0, background: "#f3f4f6" },
  elecSidebarLabel: { flex: 1, fontSize: "13.5px", fontWeight: 700, color: "#111827" },
  elecSidebarBadge: { minWidth: "18px", height: "18px", padding: "0 5px", borderRadius: "999px", background: "#16a34a", color: "#fff", fontSize: "11px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" },

  elecMain: { minWidth: 0 },
  elecMainHeader: { display: "flex", alignItems: "center", gap: "14px", marginBottom: "16px" },
  elecMainImg: { width: "56px", height: "56px", borderRadius: "12px", objectFit: "cover", background: "#f3f4f6", flexShrink: 0 },
  elecMainTitle: { fontSize: "20px", fontWeight: 800, color: "#111827" },
  elecMainSub: { fontSize: "13px", color: "#6b7280", marginTop: "2px" },

  elecDetailCard: { display: "flex", alignItems: "center", gap: "16px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px", boxShadow: "0 2px 8px rgba(15,23,42,0.05)" },
  elecDetailImg: { width: "88px", height: "88px", borderRadius: "12px", objectFit: "cover", background: "#f3f4f6", flexShrink: 0 },
  elecDetailBody: { flex: 1, minWidth: 0 },
  elecDetailTitle: { fontSize: "15px", fontWeight: 800, color: "#111827" },
  elecDetailDesc: { fontSize: "13px", color: "#6b7280", marginTop: "4px", lineHeight: 1.4 },
  elecDetailAction: { flexShrink: 0, minWidth: "150px", textAlign: "right" },

  elecCartPanel: { position: "sticky", top: "20px", display: "flex", flexDirection: "column", gap: "14px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  elecCartHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  elecCartTitle: { fontSize: "15px", fontWeight: 800, color: "#111827" },
  elecCartClearBtn: { background: "none", border: "none", color: "#4f46e5", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", padding: 0 },
  elecCartEmpty: { fontSize: "13px", color: "#9ca3af", lineHeight: 1.5, padding: "8px 0 4px" },
  elecCartList: { display: "flex", flexDirection: "column", gap: "12px", maxHeight: "320px", overflowY: "auto" },
  elecCartRow: { display: "flex", alignItems: "flex-start", gap: "10px" },
  elecCartImg: { width: "44px", height: "44px", borderRadius: "10px", objectFit: "cover", background: "#f3f4f6", flexShrink: 0 },
  elecCartRowBody: { flex: 1, minWidth: 0 },
  elecCartRowName: { fontSize: "12.5px", fontWeight: 700, color: "#111827", lineHeight: 1.3 },
  elecConsultationBadge: { display: "inline-block", marginLeft: "6px", fontSize: "9.5px", fontWeight: 800, color: "#9a3412", background: "#ffedd5", borderRadius: "999px", padding: "1px 7px", verticalAlign: "middle" },
  elecCartRowPrice: { fontSize: "12.5px", fontWeight: 800, color: "#16a34a", margin: "2px 0 6px" },
  elecCartRemoveBtn: { width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "#fee2e2", color: "#b91c1c", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 },
  elecCartTotalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "14px", fontWeight: 800, color: "#111827", paddingTop: "10px", borderTop: "1px solid #eceef5" },

  // Hidden by default (display:none) — the cn-elec-mobile-bar media query
  // above flips it to display:flex at ≤767px, where the full elecCartPanel
  // is hard to reach without scrolling the whole page.
  // paddingRight clears the site-wide floating Chat bubble, which sits
  // bottom-right in the exact same corner this bar's Continue button would
  // otherwise land in.
  elecMobileBar: { display: "none", position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 50, background: "#fff", borderTop: "1px solid #e5e7eb", boxShadow: "0 -4px 16px rgba(15,23,42,0.08)", padding: "12px 90px 12px 16px", alignItems: "center", justifyContent: "space-between", gap: "14px" },
  elecMobileBarInfo: { display: "flex", flexDirection: "column" },
  elecMobileBarCount: { fontSize: "11.5px", color: "#64748b", fontWeight: 700 },
  elecMobileBarAmount: { fontSize: "17px", fontWeight: 800, color: "#152238" },
  elecMobileBarBtn: { display: "flex", alignItems: "center", gap: "6px", border: "none", background: "#16a34a", color: "#fff", fontWeight: 800, fontSize: "14px", padding: "12px 22px", borderRadius: "12px", cursor: "pointer", whiteSpace: "nowrap" },

  elecPromiseBox: { background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "14px", padding: "14px 16px" },
  elecPromiseTitle: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  elecPromiseList: { margin: 0, padding: "0 0 0 20px", display: "flex", flexDirection: "column", gap: "4px", fontSize: "12.5px", color: "#374151" },

  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.58)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" },
  modalCard: { width: "100%", maxWidth: "560px", maxHeight: "88vh", overflowY: "auto", background: "#fff", borderRadius: "20px", padding: "20px" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" },
  modalTitle: { margin: 0, fontSize: "17px", fontWeight: 800, color: "#111827" },
  modalSub: { fontSize: "12px", color: "#9ca3af", marginTop: "4px" },
  modalCloseBtn: { width: "30px", height: "30px", borderRadius: "50%", border: "none", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#374151", flexShrink: 0 },
  modalBody: { display: "flex", flexDirection: "column", gap: "10px" },

  socketOptionCard: { display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "12px 14px" },
  socketOptionTitle: { fontSize: "13.5px", fontWeight: 700, color: "#111827" },
  socketOptionPrice: { fontSize: "12px", color: "#16a34a", fontWeight: 700, marginTop: "2px" },
  socketOptionActions: { display: "flex", alignItems: "center", gap: "10px" },
  qtyValueText: { minWidth: "20px", textAlign: "center", fontSize: "13px", fontWeight: 700, color: "#111827" },

  // Switch & Socket's 5-option center list + its rating/quantity/material
  // popup. Boxed card per row (image left, info middle, price + an inline
  // quantity stepper right) — the stepper quick-adds/removes the Normal +
  // Company-material variant directly; "View details & ratings" still opens
  // the full popup for Heavy/Not Sure/Customer material.
  ssOptionList: { display: "flex", flexDirection: "column", gap: "14px" },
  ssOptionRow: { display: "flex", alignItems: "flex-start", gap: "16px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px", boxShadow: "0 2px 8px rgba(15,23,42,0.05)" },
  ssOptionImg: { width: "100px", height: "100px", borderRadius: "14px", objectFit: "cover", display: "block", flexShrink: 0, background: "#f3f4f6" },
  ssOptionText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" },
  ssOptionLabel: { fontSize: "16px", fontWeight: 800, color: "#152238" },
  // Shared by both the row and the popup header — real average_rating/
  // review_count from ratings_reviews_col (see seed_ratings_reviews.py).
  ssOptionRating: { fontSize: "12.5px", fontWeight: 700, color: "#92400e" },
  ssOptionDesc: { fontSize: "13px", color: "#64748b", lineHeight: 1.4 },
  ssOptionChipRow: { display: "flex", flexWrap: "wrap", gap: "8px", margin: "2px 0" },
  ssOptionChip: { display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "11.5px", fontWeight: 700, color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "5px 9px" },
  ssOptionDetailsLink: { border: "none", background: "none", padding: 0, marginTop: "2px", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer", textAlign: "left", fontFamily: "inherit" },
  ssOptionRight: { flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "10px" },
  ssOptionPriceValue: { fontSize: "16px", fontWeight: 800, color: "#152238" },
  ssOptionPriceUnit: { fontSize: "11px", color: "#94a3b8", marginTop: "-6px" },
  ssOptionQtyRow: { display: "flex", alignItems: "center", gap: "8px" },
  ssConsultationNote: { fontSize: "13px", color: "#6b7280", lineHeight: 1.6, background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "12px", padding: "12px 14px" },
  ssFieldLabel: { fontSize: "12.5px", fontWeight: 800, color: "#111827", marginTop: "4px" },
  ssNumberInput: { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: "10px", border: "1.5px solid #e2e8f0", fontSize: "14px", fontFamily: "inherit", marginBottom: "4px" },

  // "View details & ratings" popup — photo, notes/instructions, sample reviews.
  ssDetailsPhoto: { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: "14px", display: "block", background: "#f3f4f6" },
  ssNotesList: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "6px" },
  ssNotesItemIncluded: { display: "flex", alignItems: "flex-start", gap: "7px", fontSize: "13px", color: "#374151", lineHeight: 1.4 },
  ssNotesItemExcluded: { display: "flex", alignItems: "flex-start", gap: "7px", fontSize: "13px", color: "#9ca3af", lineHeight: 1.4 },
  ssReviewList: { display: "flex", flexDirection: "column", gap: "8px" },
  ssReviewCard: { background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "12px", padding: "10px 14px" },
  ssReviewTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" },
  ssReviewName: { fontSize: "13px", fontWeight: 800, color: "#111827" },
  ssReviewStars: { fontSize: "12px", color: "#f59e0b", letterSpacing: "1px" },
  ssReviewComment: { fontSize: "13px", color: "#4b5563", lineHeight: 1.5 },
  ssRatingRow: { display: "flex", gap: "8px", flexWrap: "wrap" },
  ssRatingBtn: { display: "flex", alignItems: "center", gap: "6px", padding: "9px 14px", borderRadius: "999px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  ssRatingBtnActive: { display: "flex", alignItems: "center", gap: "6px", padding: "9px 14px", borderRadius: "999px", border: "1.5px solid #16a34a", background: "#16a34a", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  ssRatingPrice: { fontSize: "11.5px", opacity: 0.85 },
  ssQtyRow: { display: "flex", alignItems: "center", gap: "12px" },
  ssMaterialRow: { display: "flex", gap: "8px", flexWrap: "wrap" },
  ssMaterialBtn: { display: "flex", alignItems: "center", gap: "6px", padding: "9px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  ssMaterialBtnActive: { display: "flex", alignItems: "center", gap: "6px", padding: "9px 14px", borderRadius: "12px", border: "1.5px solid #16a34a", background: "#f0fdf4", color: "#15803d", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  ssAddBtn: { marginTop: "6px", border: "none", background: "#16a34a", color: "#fff", fontWeight: 800, fontSize: "14px", padding: "13px 0", borderRadius: "12px", cursor: "pointer" },
  ssAddBtnDisabled: { marginTop: "6px", border: "none", background: "#9ca3af", color: "#fff", fontWeight: 800, fontSize: "14px", padding: "13px 0", borderRadius: "12px", cursor: "not-allowed" },
};

export default ScheduleBookingPage;
